// #region: Class definition *********************************************************************/
/**
 * Custom web component that renders a date/time picker with localization support.
 * Extends the WebComponent base class and uses dating.js for date parsing functionality.
 * @class DateTimePicker
 * @extends WebComponent
 * 
 * @example
 * <datetime-picker 
 *     id="unique-id"
 *     name="formFieldName"
 *     value="2024-01-15T14:30:00"
 *     min="2024-01-01T00:00:00"
 *     max="2024-12-31T23:59:59"
 *     placeholder="Select date and time"
 *     format="yyyy-MM-dd HH:mm:ss"
 *     locale="nl-NL"
 *     mode="date-time"
 *     toggle-icon="lf-icon-calendar-month"
 *     show-week-numbers
 *     show-seconds
 *     disabled
 *     read-only>
 * </datetime-picker>
 * 
 * @property {string} id - Unique identifier for the component.
 * @property {string} name - Form field name for form submission.
 * @property {string} value - Always a string. For date-only mode: 'YYYY-MM-DD' (timezone-free). For date-time/time modes: ISO-like local string.
 * @property {string} min - Minimum allowed datetime (ISO 8601 datetime string).
 * @property {string} max - Maximum allowed datetime (ISO 8601 datetime string).
 * @property {string} placeholder - Placeholder text for the input.
 * @property {string} format - Display format for the datetime.
 * @property {string} locale - Locale for localization (e.g., 'en-US', 'nl-NL', 'nl').
 * @property {string} mode - Picker mode ('date', 'time', or 'date-time'). Default is 'date-time'.
 * @property {string} toggle-icon - CSS class for custom toggle icon. If not provided, uses UTF-8 defaults: '🕐' for time, '📅' for date/date-time.
 * @property {boolean} show-week-numbers - Whether to display week numbers in the calendar.
 * @property {boolean} show-seconds - Whether to show seconds input in time picker.
 * @property {boolean} disabled - Whether the picker is disabled.
 * @property {boolean} read-only - Whether the picker is read-only.
 * 
 * @remarks All attributes are optional
 */
class DateTimePicker extends WebComponent {

    // #region: Static Data **********************************************************************/
    /**
     * Timeout to allow other elements to receive focus first.
     * @type {number}
     * @static
     * @memberof DateTimePicker
     */
    static BLUR_TIMEOUT = 10;

    /**
     * Offset from viewport edge for popup positioning.
     * @type {number}
     * @static
     * @memberof DateTimePicker
     */
    static EDGE_OFFSET = 10;

    /**
     * Maximum hours in 24-hour format.
     * @type {number}
     * @static
     * @memberof DateTimePicker
     */
    static HOURS_24 = 23;

    /**
     * Minimum height for the dialog popup.
     * @type {number}
     * @static
     * @memberof DateTimePicker
     */
    static MIN_DIALOG_HEIGHT = 290;

    /**
     * Minimum width for the dialog popup.
     * @type {number}
     * @static
     * @memberof DateTimePicker
     */
    static MIN_DIALOG_WIDTH = 290;

    /**
     * Maximum minutes value.
     * @type {number}
     * @static
     * @memberof DateTimePicker
     */
    static MINUTES = 59;

    /**
     * Available picker modes.
     * @type {Object}
     * @static
     * @memberof DateTimePicker
     */
    static MODES = {
        DATE: 'date',
        DATE_TIME: 'date-time',
        TIME: 'time'
    };

    /**
     * Total number of cells in the calendar grid (6 weeks × 7 days).
     * @type {number}
     * @static
     * @memberof DateTimePicker
     */
    static MONTH_GRID_SIZE = 42;

    /**
     * Maximum month index (December).
     * @type {number}
     * @static
     * @memberof DateTimePicker
     */
    static MONTHS = 11;

    /**
     * Hour value representing noon.
     * @type {number}
     * @static
     * @memberof DateTimePicker
     */
    static NOON = 12;

    /**
     * Scroll offset for popup positioning.
     * @type {number}
     * @static
     * @memberof DateTimePicker
     */
    static SCROLL_OFFSET = 0;

    /**
     * Maximum seconds value.
     * @type {number}
     * @static
     * @memberof DateTimePicker
     */
    static SECONDS = 59;

    /**
     * Wait for transition to complete before hiding. Match CSS transition duration.
     * @type {number}
     * @static
     * @memberof DateTimePicker
     */
    static TRANSITION_TIMEOUT = 200;

    /**
     * Default offsetHeight to use for time wheel positioning.
     * @type {number}
     * @static
     * @memberof DateTimePicker
     */
    static WHEEL_OFFSET_HEIGHT = 10;

    /**
     * Number of days in a week.
     * @type {number}
     * @static
     * @memberof DateTimePicker
     */
    static WEEKDAYS = 7;

    /**
     * Number of year buttons in the year selector.
     * @type {number}
     * @static
     * @memberof DateTimePicker
     */
    static YEAR_GRID = 12;

    /**
     * CSS selector string for focusable elements.
     * @type {string}
     * @static
     * @memberof DateTimePicker
     */
    static FOCUSABLE_SELECTOR = 'a[href], area[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    // #endregion ********************************************************************************/

    // #region: Private Fields *******************************************************************/
    /**
     * Currently active view (calendar or time).
     * @type {string}
     * @private
     * @memberof DateTimePicker
     */
    #activeView;

    /**
     * Currently displayed month (0-11).
     * @type {number}
     * @private
     * @memberof DateTimePicker
     */
    #currentMonth;

    /**
     * Currently displayed year.
     * @type {number}
     * @private
     * @memberof DateTimePicker
     */
    #currentYear;

    /**
     * References to DOM elements.
     * @type {Object}
     * @private
     * @memberof DateTimePicker
     */
    #elements;

    /**
     * Cache for DOM element pools to avoid repeated creation/destruction.
     * @type {Object}
     * @private
     * @memberof DateTimePicker
     */
    #elementCache;

    /**
     * Format string for displaying datetime.
     * @type {string}
     * @private
     * @memberof DateTimePicker
     */
    #format;

    /**
     * Whether the component is currently in month selection mode.
     * @type {boolean}
     * @private
     * @memberof DateTimePicker
     */
    #isInMonthSelector;

    /**
     * Whether the component is currently in year selection mode.
     * @type {boolean}
     * @private
     * @memberof DateTimePicker
     */
    #isInYearSelector;

    /**
     * Whether the popup is currently open.
     * @type {boolean}
     * @private
     * @memberof DateTimePicker
     */
    #isOpen;

    /**
     * Map of event listeners for cleanup.
     * @type {Map}
     * @private
     * @memberof DateTimePicker
     */
    #listeners;

    /**
     * Cached locale resources for this component.
     * @type {Object}
     * @private
     * @memberof DateTimePicker
     */
    #locales;

    /**
     * Maximum allowed datetime.
     * @type {Date|null}
     * @private
     * @memberof DateTimePicker
     */
    #max;

    /**
     * Minimum allowed datetime.
     * @type {Date|null}
     * @private
     * @memberof DateTimePicker
     */
    #min;

    /**
     * Cached normalized maximum datetime for date-only mode comparisons.
     * @type {Date|null}
     * @private
     * @memberof DateTimePicker
     */
    #normalizedMax;

    /**
     * Cached normalized minimum datetime for date-only mode comparisons.
     * @type {Date|null}
     * @private
     * @memberof DateTimePicker
     */
    #normalizedMin;

    /**
     * Current picker mode (date, time, or date-time).
     * @type {string}
     * @private
     * @memberof DateTimePicker
     */
    #mode;

    /**
     * Observer for element resize events.
     * @type {ResizeObserver|null}
     * @private
     * @memberof DateTimePicker
     */
    #resizeObserver;

    /**
     * Currently selected date.
     * @type {Date|null}
     * @private
     * @memberof DateTimePicker
     */
    #selectedDate;

    /**
     * Currently selected time components.
     * @type {Object}
     * @private
     * @memberof DateTimePicker
     */
    #selectedTime;

    /**
     * Whether to show week numbers in calendar.
     * @type {boolean}
     * @private
     * @memberof DateTimePicker
     */
    #showWeekNumbers;

    /**
     * Whether to show seconds in time input.
     * @type {boolean}
     * @private
     * @memberof DateTimePicker
     */
    #showSeconds;

    /**
     * Current datetime value.
     * @type {Date|null}
     * @private
     * @memberof DateTimePicker
     */
    #value;

    /**
     * Timeout for wheel scroll events.
     * @type {number|null}
     * @private
     * @memberof DateTimePicker
     */
    #wheelScrollTimeout;

    /**
     * Starting position for wheel touch events.
     * @type {Object|null}
     * @private
     * @memberof DateTimePicker
     */
    #wheelTouchStart;
    // #endregion ********************************************************************************/

    // #region: Constructor **********************************************************************/
    /**
     * Initializes a new DateTimePicker instance.
     * Sets up private fields, registers locale namespaces, and configures default values.
     * 
     * @constructor
     * @memberof DateTimePicker
     */
    constructor() {
        super();

        // Register locale namespace used by this component and cache resources.
        this.#locales = this.registerLocaleNamespace();

        // Initialize private fields.
        this.#value = null;
        this.#min = null;
        this.#max = null;
        this.#normalizedMax = null;
        this.#normalizedMin = null;
        this.#format = 'yyyy-MM-dd HH:mm:ss';
        this.#mode = DateTimePicker.MODES.DATE_TIME;
        this.#showWeekNumbers = false;
        this.#showSeconds = false;
        this.#isInMonthSelector = false;
        this.#isInYearSelector = false;
        this.#isOpen = false;
        this.#activeView = 'calendar';
        this.#currentMonth = new Date().getMonth();
        this.#currentYear = new Date().getFullYear();
        this.#selectedDate = null;
        this.#selectedTime = { hours: 0, minutes: 0, seconds: 0 };
        this.#elementCache = {
            dayButtons: [],
            monthButtons: [],
            weekHeaders: [],
            weekNumbers: [],
            yearButtons: []
        };
        this.#elements = {};
        this.#listeners = new Map();
        this.#resizeObserver = null;
        this.#wheelScrollTimeout = null;
        this.#wheelTouchStart = null;
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Registered tag name for this web component.
     * @type {string}
     * @static
     * @memberof DateTimePicker
     */
    static tagName = 'datetime-picker';

    /**
     * Returns the string representation of this class instance.
     * @returns {string} The string representation of this class.
     * @memberof DateTimePicker
     */
    toString() {
        return '[DateTimePicker]';
    }

    /**
     * Returns the string representation of this class.
     * @returns {string} The string representation of this class.
     * @static
     * @memberof DateTimePicker
     */
    static toString() {
        return '[DateTimePicker]';
    }

    /**
     * Returns the version number of this class.
     * @returns {string} The version number of this class.
     * @static
     * @memberof DateTimePicker
     */
    static version() {
        return '1.0.1';
    }
    // #endregion ********************************************************************************/

    // #region: Static Properties ****************************************************************/
    /**
     * Defines which attributes should trigger the attributeChangedCallback when modified.
     * @returns {string[]} Array of attribute names to observe.
     * @static
     * @memberof DateTimePicker
     */
    static get observedAttributes() {
        return [...(super.observedAttributes || []), 'disabled', 'format', 'max', 'min', 'mode', 'name', 'placeholder', 'read-only', 'show-seconds', 'show-week-numbers', 'toggle-icon', 'value'];
    }
    // #endregion ********************************************************************************/

    // #region: Properties ***********************************************************************/
    /**
     * Gets the current picker mode.
     * @returns {string} The current picker mode.
     * @memberof DateTimePicker
     */
    get mode() {
        return this.#mode;
    }

    /**
     * Sets the picker mode.
     * @param {string} val The mode to set ('date', 'time', or 'date-time').
     * @memberof DateTimePicker
     */
    set mode(val) {
        const validModes = Object.values(DateTimePicker.MODES);

        this.#mode = validModes.includes(val) ? val : DateTimePicker.MODES.DATE_TIME;
        this.#updateNormalizedBounds();
        this.#updateModeUI();
    }

    /**
     * Gets whether week numbers are shown in the calendar.
     * @returns {boolean} True if week numbers are shown.
     * @memberof DateTimePicker
     */
    get showWeekNumbers() {
        return this.#showWeekNumbers;
    }

    /**
     * Sets whether to show week numbers in the calendar.
     * @param {boolean} val Whether to show week numbers.
     * @memberof DateTimePicker
     */
    set showWeekNumbers(val) {
        this.#showWeekNumbers = Boolean(val);
        this.#updateWeekNumbersUI();
    }

    /**
     * Gets the current datetime value as a string.
     * For date-only mode, returns timezone-free 'YYYY-MM-DD' string.
     * For date-time and time modes, returns ISO-like local string.
     * @returns {string} The current datetime value as string, or null if no value.
     * @memberof DateTimePicker
     */
    get value() {
        if (!this.#value) {
            return null;
        }

        // For date-only mode, return timezone-free date string.
        if (this.#mode === DateTimePicker.MODES.DATE) {
            return this.#formatDate(this.#value);
        }

        // For date-time and time modes, return ISO-like local string.
        return this.#formatISODateTime(this.#value);
    }

    /**
     * Sets the current datetime value from an ISO string.
     * @param {string|null} val The datetime value to set.
     * @memberof DateTimePicker
     */
    set value(val) {
        if (val) {
            const date = this.#parseISODate(val);

            if (date && !isNaN(date.getTime())) {
                this.#value = date;
                this.#updateFromValue();
            }
        }
        else {
            this.#value = null;
            this.#updateFromValue();
        }
    }
    // #endregion ********************************************************************************/

    // #region: Lifecycle events *****************************************************************/
    /**
     * Processes changes to any of the observed attributes.
     * @param {string} name The name of the attribute that changed.
     * @param {string} oldValue The previous value of the attribute.
     * @param {string} newValue The new value of the attribute.
     * @memberof DateTimePicker
     */
    attributeChangedCallback(name, oldValue, newValue) {
        // Call parent's implementation.
        super.attributeChangedCallback(name, oldValue, newValue);

        if (oldValue === newValue) {
            return;
        }

        switch (name) {

            case 'disabled':
                this.#updateDisabledState();

                break;

            case 'format':
                this.#format = newValue || this.#getDefaultFormat();
                this.#updateInputDisplay();

                break;

            case 'show-seconds':
                this.#showSeconds = newValue !== null;

                // Update format if it wasn't explicitly set.
                if (!this.hasAttribute('format')) {
                    this.#format = this.#getDefaultFormat();
                }

                this.#updateTimeUI();
                this.#updateInputDisplay();

                break;

            case 'max':
                this.#max = newValue ? this.#parseISODate(newValue) : null;

                break;

            case 'min':
                this.#min = newValue ? this.#parseISODate(newValue) : null;

                break;

            case 'mode':
                this.mode = newValue;

                break;

            case 'name':
                if (this.#elements.input) {
                    this.#elements.input.name = newValue || '';
                }

                break;

            case 'placeholder':
                if (this.#elements.input) {
                    this.#elements.input.placeholder = newValue || '';
                }

                break;

            case 'read-only':
                this.#updateReadonlyState();

                break;

            case 'show-week-numbers':
                this.showWeekNumbers = newValue !== null;

                break;

            case 'value':
                this.value = newValue;

                break;

            default:
                break;

        }
    }

    /**
     * Called when the custom element is attached to the DOM.
     * 
     * @memberof DateTimePicker
     */
    connectedCallback() {
        super.connectedCallback();

        // Set mode from attribute if provided.
        const modeValue = this.getAttribute('mode');

        if (modeValue) {
            this.mode = modeValue;
        }

        // Set show-week-numbers from attribute if provided.
        this.showWeekNumbers = this.hasAttribute('show-week-numbers');

        // Basic state.
        this.#isInMonthSelector = false;
        this.#isInYearSelector = false;
    }

    /**
     * Cleanup. Called when the component is removed from the DOM.
     * 
     * @memberof DateTimePicker
     */
    disconnectedCallback() {
        this.#removeEventListeners();
        this.#cleanupResizeObserver();
        this.#clearElementCache();

        // Remove popup from body if it exists.
        if (this.#elements.popup && this.#elements.popup.parentNode === document.body) {
            document.body.removeChild(this.#elements.popup);
        }

        super.disconnectedCallback();
    }

    /**
     * Initialize the UI (on 'DOMContentLoaded').
     * 
     * @memberof DateTimePicker
     */
    initialize() {
        this.#render();

        // Attach event listeners to elements.
        // Toggle button.
        this.#addListener(this.#elements.toggle, 'click', () => {
            return this.toggle();
        });

        // Input events.
        this.#addListener(this.#elements.input, 'keydown', (event) => {
            return this.#handleInputKeydown(event);
        });

        this.#addListener(this.#elements.input, 'blur', () => {
            // Use setTimeout to allow other elements to receive focus first.
            // This prevents unnecessary validation when user clicks within the component.
            setTimeout(() => {
                // Only handle the change if focus has moved outside the component entirely.
                const { activeElement } = document;
                const focusWithinComponent = this.contains(activeElement) ||
                    (this.#elements.popup && this.#elements.popup.contains(activeElement));

                if (!focusWithinComponent) {
                    this.#handleInputValueChange();
                }
            }, DateTimePicker.BLUR_TIMEOUT);
        });

        // Tab navigation (only if tabs exist).
        if (this.#elements.calendarTab) {
            this.#addListener(this.#elements.calendarTab, 'click', () => {
                return this.#setActiveView('calendar');
            });
        }

        if (this.#elements.timeTab) {
            this.#addListener(this.#elements.timeTab, 'click', () => {
                return this.#setActiveView('time');
            });
        }

        // Calendar navigation (only if calendar exists).
        if (!this.#elements.prevNav) {
            this.#elements.prevNav = this.#elements.popup.querySelector('[data-nav="prev"]');
        }

        if (!this.#elements.nextNav) {
            this.#elements.nextNav = this.#elements.popup.querySelector('[data-nav="next"]');
        }

        const { prevNav } = this.#elements;
        const { nextNav } = this.#elements;

        if (prevNav) {
            this.#addListener(prevNav, 'click', () => {
                return this.#navigateDate(-1);
            });
        }

        if (nextNav) {
            this.#addListener(nextNav, 'click', () => {
                return this.#navigateDate(1);
            });
        }

        // Month/year selector.
        if (this.#elements.monthYearDisplay) {
            this.#addListener(this.#elements.monthYearDisplay, 'click', () => {
                if (this.#isInMonthSelector) {
                    return this.#showYearSelector();
                }
                else {
                    return this.#showMonthYearSelector();
                }
            });
        }

        // Today button (only if calendar exists).
        if (this.#elements.todayButton) {
            this.#addListener(this.#elements.todayButton, 'click', () => {
                return this.#selectToday();
            });
        }

        // OK/Cancel buttons for date-time mode.
        if (this.#elements.okButton) {
            this.#addListener(this.#elements.okButton, 'click', () => {
                return this.#confirmSelection();
            });
        }

        if (this.#elements.cancelButton) {
            this.#addListener(this.#elements.cancelButton, 'click', () => {
                return this.#cancelSelection();
            });
        }

        // Now button (only if time view exists).
        if (this.#elements.nowButton) {
            this.#addListener(this.#elements.nowButton, 'click', () => {
                return this.#handleNowButtonClick();
            });
        }

        // Time inputs (only if time view exists).
        if (this.#elements.hoursInput) {
            this.#addListener(this.#elements.hoursInput, 'input', () => {
                return this.#handleTimeInput('hours');
            });
        }

        if (this.#elements.minutesInput) {
            this.#addListener(this.#elements.minutesInput, 'input', () => {
                return this.#handleTimeInput('minutes');
            });
        }

        if (this.#elements.secondsInput) {
            this.#addListener(this.#elements.secondsInput, 'input', () => {
                return this.#handleTimeInput('seconds');
            });
        }

        // Period toggle (only if time view exists).
        if (this.#elements.periodToggle) {
            this.#addListener(this.#elements.periodToggle, 'click', () => {
                return this.#togglePeriod();
            });
        }

        // Calendar grid delegation (only if calendar exists).
        if (this.#elements.calendarGrid) {
            this.#addListener(this.#elements.calendarGrid, 'click', (event) => {
                if (event.target.classList.contains('datetime-picker-day') && !event.target.classList.contains('disabled')) {
                    event.preventDefault();
                    event.stopPropagation();

                    this.#selectDate(new Date(event.target.dataset.date));
                }
            });
        }

        // Keyboard navigation.
        this.#addListener(this.#elements.popup, 'keydown', (event) => {
            return this.#handlePopupKeydown(event);
        });

        this.#updateFromValue();
        this.#updateModeUI();
        this.#updateWeekNumbersUI();

        // Ensure format is consistent with mode and show-seconds setting.
        if (!this.hasAttribute('format')) {
            this.#format = this.#getDefaultFormat();

            if (!this.#showSeconds && this.#format.includes(':ss')) {
                this.#format = this.#format.replace(':ss', '');
            }
        }

        this.#updateTimeUI();
    }

    /**
     * Called when the locale changes.
     * @param {string} oldLocale The previous locale.
     * @param {string} newLocale The new locale.
     * 
     * @memberof DateTimePicker
     */
    onLocaleChanged(oldLocale, newLocale) {
        // Update all localized content.
        this.#updateLocalizedContent();

        // Re-render calendar if it's visible.
        if (this.#activeView === 'calendar' && this.#isOpen) {
            this.#renderCalendar();
        }
    }
    // #endregion ********************************************************************************/

    // #region: Events ***************************************************************************/
    /**
     * Handles arrow key navigation in the calendar.
     * @param {KeyboardEvent} event The keyboard event.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #handleCalendarArrowNavigation(event) {
        const currentDate = new Date(event.target.dataset.date);
        const newDate = new Date(currentDate);

        switch (event.key) {

            case 'ArrowDown':
                newDate.setDate(newDate.getDate() + DateTimePicker.WEEKDAYS);

                break;

            case 'ArrowLeft':
                newDate.setDate(newDate.getDate() - 1);

                break;

            case 'ArrowRight':
                newDate.setDate(newDate.getDate() + 1);

                break;

            case 'ArrowUp':
                newDate.setDate(newDate.getDate() - DateTimePicker.WEEKDAYS);

                break;

            default:
                break;

        }

        // Update month if necessary.
        if (newDate.getMonth() !== this.#currentMonth || newDate.getFullYear() !== this.#currentYear) {
            this.#currentMonth = newDate.getMonth();
            this.#currentYear = newDate.getFullYear();
            this.#renderCalendar();
        }

        // Focus the new date.
        setTimeout(() => {
            // Use cached reference if available, otherwise query.
            const dateString = this.#formatDate(newDate);
            const newButton = this.#elements.calendarGrid.querySelector(`[data-date="${dateString}"]`);

            if (newButton) {
                newButton.focus();
            }
        }, 0);
    }

    /**
     * Handles keydown events on the input.
     * @param {KeyboardEvent} event The keyboard event.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #handleInputKeydown(event) {
        // Don't handle any keyboard events if disabled or readonly.
        if (this.disabled || this.readOnly) {
            return;
        }

        switch (event.key) {

            case 'ArrowDown':
                if (event.altKey) {
                event.preventDefault();
                this.open();
                }

                break;

            case 'Enter':
                    event.preventDefault();
                this.open();

                break;

            case 'Escape':
                if (this.#isOpen) {
                    event.preventDefault();

                    this.close();
                }

                break;

            case 'Tab':
                if (this.#isOpen && !event.shiftKey) {
                    event.preventDefault();

                    const firstFocusable = this.#elements.popup.querySelector('button:not([disabled])');

                    if (firstFocusable) {
                        firstFocusable.focus();
                    }
                }

                break;

            default:
                break;

        }
    }

    /**
     * Handles input value changes when user types directly.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #handleInputValueChange() {
        const inputValue = this.#elements.input.value.trim();

        if (!inputValue) {
            this.#value = null;
            this.#selectedDate = null;
            this.#selectedTime = { hours: 0, minutes: 0, seconds: 0 };
            this.#updateFromValue();

            return;
        }

        // Validate the input first.
        if (!this.#validateInputValue(inputValue)) {
            // Invalid input - preserve existing value instead of losing it.
            if (this.#value) {
                this.#updateInputDisplay();
            }
            
            return;
        }

        // Parse the input value using the dating library.
        const parsedDate = this.#parseInputByFormat(inputValue);

        if (parsedDate && !isNaN(parsedDate.getTime())) {
            this.#value = parsedDate;
            this.#updateFromValue();

            // Dispatch change event.
            this.#dispatchChangeEvent();
        }
    }

    /**
     * Handles keyboard navigation within the month grid.
     * @param {KeyboardEvent} event The keyboard event.
     * @param {HTMLElement} monthGrid The month grid container.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #handleMonthGridNavigation(event, monthGrid) {
        /* eslint-disable no-magic-numbers -- Date calculation magic. */
        // Cache month buttons for better performance.
        if (!this.#elements.monthButtons) {
            this.#elements.monthButtons = Array.from(monthGrid.querySelectorAll('.datetime-picker-month-button'));
        }

        const buttons = this.#elements.monthButtons;
        const currentIndex = buttons.indexOf(event.target);

        let newIndex = currentIndex;

        switch (event.key) {

            case 'ArrowLeft':
                newIndex = currentIndex > 0 ? currentIndex - 1 : buttons.length - 1;

                break;

            case 'ArrowRight':
                newIndex = currentIndex < buttons.length - 1 ? currentIndex + 1 : 0;

                break;

            case 'ArrowUp':
                // Move up by 3 months (3 columns in grid).
                newIndex = currentIndex - 3;

                if (newIndex < 0) {
                    newIndex = currentIndex + 9;

                    if (newIndex >= buttons.length) {
                        newIndex = buttons.length - 1;
                    }
                }

                break;

            case 'ArrowDown':
                // Move down by 3 months (3 columns in grid).
                newIndex = currentIndex + 3;

                if (newIndex >= buttons.length) {
                    newIndex = currentIndex - 9;

                    if (newIndex < 0) {
                        newIndex = 0;
                    }
                }

                break;

            default:
                break;
        
        }

        if (newIndex !== currentIndex && buttons[newIndex]) {
            buttons[newIndex].focus();
        }
        /* eslint-enable no-magic-numbers */
    }

    /**
     * Handles Now button click to set current time.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #handleNowButtonClick() {
        const now = new Date();
        this.#selectedTime = {
            hours: now.getHours(),
            minutes: now.getMinutes(),
            seconds: now.getSeconds()
        };

        this.#updateTimeDisplay();
        this.#updateWheelPositions();
        this.#updateValue();
    }

    /**
     * Closes the popup if a click is detected outside of the component.
     * @param {MouseEvent} event The mouse event.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #handleOutsideClick = (event) => {
        // Check if click is outside both the component and the popup.
        const clickedInsideComponent = this.contains(event.target);
        const clickedInsidePopup = this.#elements.popup && this.#elements.popup.contains(event.target);

        if (!clickedInsideComponent && !clickedInsidePopup && this.#isOpen) {
            // Check if the clicked element is interactive (focusable).
            const isInteractive = coreUI.isInteractive(event.target);

            // Don't refocus input if user clicked on an interactive element.
            this.close(!isInteractive);
        }
    };

    /**
     * Handles keydown events within the popup.
     * @param {KeyboardEvent} event The keyboard event.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #handlePopupKeydown(event) {
        switch (event.key) {

            case 'ArrowDown':
            case 'ArrowLeft':
            case 'ArrowRight':
            case 'ArrowUp':
                if (this.#activeView === 'calendar' && event.target.classList.contains('datetime-picker-day')) {
                    event.preventDefault();

                    this.#handleCalendarArrowNavigation(event);
                }

                break;

            case 'Escape':
                event.preventDefault();

                this.close();

                break;

            case 'Tab':
                this.#handleTabNavigation(event);

                break;

            default:
                break;

        }
    }

    /**
     * Handles keyboard tab navigation within the popup.
     * Redirects focus to the input if tabbing out of bounds.
     * @param {KeyboardEvent} event The keyboard event.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #handleTabNavigation(event) {
        const focusableElements = Array.from(this.#elements.popup.querySelectorAll(DateTimePicker.FOCUSABLE_SELECTOR));
        const currentIndex = focusableElements.indexOf(event.target);

        if (currentIndex === -1 || focusableElements.length === 0) {
            // Let browser manage.
            return;
        }

        const isAtEdge = event.shiftKey ? currentIndex === 0 : currentIndex === focusableElements.length - 1;

        if (isAtEdge) {
            event.preventDefault();

            this.#elements.input.focus();
        }
    }

    /**
     * Handles time input changes.
     * @param {string} type The time type (hours, minutes, seconds).
     * 
     * @private
     * @memberof DateTimePicker
     */
    #handleTimeInput(type) {
        const input = this.#elements[`${type}Input`];
        const max = parseInt(input.max);

        let value = parseInt(input.value) || 0;

        // Clamp value.
        if (value < 0) {
            value = 0;
        }

        if (value > max) {
            value = max;
        }

        // Update selected time.
        this.#selectedTime[type] = value;

        // Update value.
        if (!this.#value) {
            this.#value = new Date();

            // For time-only mode, set to today.
            if (this.#mode === DateTimePicker.MODES.TIME) {
                const today = new Date();

                this.#value.setFullYear(today.getFullYear());
                this.#value.setMonth(today.getMonth());
                this.#value.setDate(today.getDate());
            }
        }

        if (type === 'hours') {
            this.#value.setHours(value);
        }
        else if (type === 'minutes') {
            this.#value.setMinutes(value);
        }
        else if (type === 'seconds') {
            this.#value.setSeconds(value);
        }

        // Update displays.
        this.#updateTimeDisplay();
        this.#updateInputDisplay();

        // Dispatch change event.
        this.#dispatchChangeEvent();
    }

    /**
     * Handles wheel scroll events for time selection.
     * @param {Event} event The scroll event.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #handleWheelScroll(event) {
        const wheel = event.target.closest('.datetime-picker-wheel');
        if (!wheel) {
            return;
        }

        // Debounce scroll events to avoid excessive updates.
        clearTimeout(this.#wheelScrollTimeout);
        this.#wheelScrollTimeout = setTimeout(() => {
            this.#updateWheelSelection(wheel, false);
            this.#snapWheelToSelection(wheel);
        }, DateTimePicker.TRANSITION_TIMEOUT);
    }

    /**
     * Handles wheel touch end events.
     * @param {TouchEvent} event The touch event.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #handleWheelTouchEnd(event) {
        /* eslint-disable no-magic-numbers -- Date calculation magic. */
        const wheel = event.currentTarget;
        if (!this.#wheelTouchStart) {
            return;
        }

        const deltaTime = Date.now() - this.#wheelTouchStart.startTime;
        const deltaY = event.changedTouches[0].clientY - this.#wheelTouchStart.startY;

        // Add inertia for smooth scrolling.
        if (deltaTime < 300 && Math.abs(deltaY) > 20) {
            const velocity = deltaY / deltaTime;
            const inertiaDistance = velocity * 200;
            wheel.scrollTop -= inertiaDistance;
        }

        wheel.style.scrollBehavior = 'smooth';
        this.#wheelTouchStart = null;

        // Update selection after touch ends.
        setTimeout(() => {
            this.#updateWheelSelection(wheel);
            this.#snapWheelToSelection(wheel);
        }, 100);

        /* eslint-enable no-magic-numbers */
    }

    /**
     * Handles wheel touch move events.
     * @param {TouchEvent} event The touch event.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #handleWheelTouchMove(event) {
        event.preventDefault();

        const wheel = event.currentTarget;

        if (!this.#wheelTouchStart) {
            return;
        }

        const deltaY = event.touches[0].clientY - this.#wheelTouchStart.startY;
        wheel.scrollTop = this.#wheelTouchStart.startScrollTop - deltaY;
    }

    /**
     * Handles wheel touch start events.
     * @param {TouchEvent} event The touch event.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #handleWheelTouchStart(event) {
        const wheel = event.currentTarget;

        this.#wheelTouchStart = {
            startScrollTop: wheel.scrollTop,
            startTime: Date.now(),
            startY: event.touches[0].clientY
        };
        wheel.style.scrollBehavior = 'auto';
    }

    /**
     * Handles keyboard navigation within the year grid.
     * @param {KeyboardEvent} event The keyboard event.
     * @param {HTMLElement} yearGrid The year grid container.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #handleYearGridNavigation(event, yearGrid) {
        /* eslint-disable no-magic-numbers -- Date calculation magic. */
        // Cache year buttons for better performance.
        if (!this.#elements.yearButtons) {
            this.#elements.yearButtons = Array.from(yearGrid.querySelectorAll('.datetime-picker-year-button'));
        }

        const buttons = this.#elements.yearButtons;
        const currentIndex = buttons.indexOf(event.target);

        let newIndex = currentIndex;

        switch (event.key) {

            case 'ArrowLeft':
                newIndex = currentIndex > 0 ? currentIndex - 1 : buttons.length - 1;

                break;

            case 'ArrowRight':
                newIndex = currentIndex < buttons.length - 1 ? currentIndex + 1 : 0;

                break;

            case 'ArrowUp':
                // Move up by 3 years (3 columns in grid).
                newIndex = currentIndex - 3;

                if (newIndex < 0) {
                    newIndex = currentIndex + 9;

                    if (newIndex >= buttons.length) {
                        newIndex = buttons.length - 1;
                    }
                }

                break;

            case 'ArrowDown':
                // Move down by 3 years (3 columns in grid).
                newIndex = currentIndex + 3;

                if (newIndex >= buttons.length) {
                    newIndex = currentIndex - 9;

                    if (newIndex < 0) {
                        newIndex = 0;
                    }
                }

                break;

            default:
                break;

        }

        if (newIndex !== currentIndex && buttons[newIndex]) {
            buttons[newIndex].focus();
        }
        /* eslint-enable no-magic-numbers */
    }
    // #endregion ********************************************************************************/

    // #region: Public methods *******************************************************************/
    /**
     * Closes the datetime picker popup.
     * 
     * @memberof DateTimePicker
     */
    close(shouldRefocus = true) {
        if (!this.#isOpen) {
            return;
        }

        this.#isOpen = false;
        this.#elements.popup.classList.remove('open');
        this.#elements.popup.setAttribute('aria-hidden', 'true');
        this.#elements.input.setAttribute('aria-expanded', 'false');

        // Reset month selector state to prevent corruption on reopen.
        if (this.#isInMonthSelector) {
            this.#hideMonthSelector();
        }

        // Reset year selector state to prevent corruption on reopen.
        if (this.#isInYearSelector) {
            this.#hideYearSelector();
        }

        // Cleanup resize observer.
        this.#cleanupResizeObserver();

        // Cleanup outside handler.
        document.removeEventListener('click', this.#handleOutsideClick);

        // Return focus to input only if requested and no other element has focus.
        if (shouldRefocus && (!document.activeElement || document.activeElement === document.body)) {
            this.#elements.input.focus();
        }

        // Update validation state when the control is currently in error.
        this.updateValidationState();
    }

    /**
     * Opens the datetime picker popup.
     * 
     * @memberof DateTimePicker
     */
    open() {
        if (this.#isOpen || this.disabled || this.readOnly) {
            return;
        }

        // Parse current input value before opening.
        this.#parseInputValue();

        this.#isOpen = true;

        this.#positionPopup();
        this.#elements.popup.classList.add('open');
        this.#elements.popup.setAttribute('aria-hidden', 'false');
        this.#elements.input.setAttribute('aria-expanded', 'true');

        // Set initial view based on mode.
        const initialView = this.#mode === DateTimePicker.MODES.TIME ? 'time' : 'calendar';

        this.#setActiveView(initialView);

        // Ensure calendar shows correct month/year for selected date after opening.
        if (initialView === 'calendar' && this.#value) {
            this.#currentMonth = this.#value.getMonth();
            this.#currentYear = this.#value.getFullYear();
            this.#renderCalendar();
        }

        // Sets up resize observer to reposition popup on window changes.
        this.#cleanupResizeObserver();

        // Reposition on window resize or scroll.
        const repositionHandler = () => {
            return this.#positionPopup();
        };

        this.#addListener(window, 'resize', repositionHandler);
        this.#addListener(window, 'scroll', repositionHandler);

        // Use ResizeObserver if available to detect when the input element moves.
        if (window.ResizeObserver) {
            this.#resizeObserver = new ResizeObserver(repositionHandler);
            this.#resizeObserver.observe(this.#elements.wrapper);
        }

        // Click outside to close.
        this.#addListener(document, 'click', this.#handleOutsideClick);

        // Focus first focusable element.
        setTimeout(() => {
            const firstFocusable = this.#elements.popup.querySelector(DateTimePicker.FOCUSABLE_SELECTOR);

            if (firstFocusable) {
                firstFocusable.focus();
            }
        }, 0);
    }

    /**
     * Toggles the datetime picker popup.
     * 
     * @memberof DateTimePicker
     */
    toggle() {
        if (this.#isOpen) {
            this.close();
        }
        else {
            this.open();
        }
    }

    /**
     * Validate the content.
     * @returns {boolean} True if valid, false if not.
     * 
     * @memberof DateTimePicker
     */
    validate() {
        if (!this.#value) {
            return !this.hasAttribute('required');
        }

        // Check min/max constraints.
        if (this.#mode === DateTimePicker.MODES.DATE) {
            // For date-only mode, normalize the value to start of day for comparison.
            const normalizedValue = this.#normalizeToStartOfDay(this.#value);

            if (this.#normalizedMin && normalizedValue < this.#normalizedMin) {
                return false;
            }

            if (this.#normalizedMax && normalizedValue > this.#normalizedMax) {
                return false;
            }
        }
        else {
            // For datetime modes, use exact time comparisons.
            if (this.#min && this.#value < this.#min) {
                return false;
            }

            if (this.#max && this.#value > this.#max) {
                return false;
            }
        }

        // Use dating library to validate the formatted string.
        const formattedValue = window.dating.formatDate(this.#value.toISOString(), this.#format);

        return this.#validateInputValue(formattedValue);
    }
    // #endregion ********************************************************************************/

    // #region: Private methods ******************************************************************/
    /**
     * Adds an event listener and tracks it for cleanup.
     * @param {Element} element The element to attach the listener to.
     * @param {string} event The event name.
     * @param {Function} handler The event handler.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #addListener(element, event, handler) {
        element.addEventListener(event, handler);

        if (!this.#listeners.has(element)) {
            this.#listeners.set(element, []);
        }

        this.#listeners.get(element).push({ event, handler });
    }

    /**
     * Cancels the selection and closes the picker.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #cancelSelection() {
        // Revert to original value if it exists.
        if (this.#value) {
            this.#updateFromValue();
        }

        this.close();
    }

    /**
     * Cleans up resize observer and related listeners.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #cleanupResizeObserver() {
        if (this.#resizeObserver) {
            this.#resizeObserver.disconnect();
            this.#resizeObserver = null;
        }
    }

    /**
     * Clears the element cache to free memory.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #clearElementCache() {
        Object.keys(this.#elementCache).forEach((key) => {
            this.#elementCache[key].length = 0;
        });
    }

    /**
     * Confirms the current selection and closes the picker.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #confirmSelection() {
        // If time is selected but no date is present, use today's date.
        if (this.#mode === DateTimePicker.MODES.TIME || (this.#mode === DateTimePicker.MODES.DATE_TIME && !this.#selectedDate)) {
            // Check if we have time values selected.
            const hasTimeSelection = this.#selectedTime &&
                (this.#selectedTime.hours !== undefined ||
                 this.#selectedTime.minutes !== undefined ||
                 this.#selectedTime.seconds !== undefined);
            
            if (hasTimeSelection) {
                // Use today's date with the selected time.
                const today = new Date();

                this.#selectedDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                
                // Update the value.
                this.#updateValue();
            }
        }
        
        this.close();
        this.#dispatchChangeEvent();
    }

    /**
     * Creates the calendar view.
     * @returns {HTMLElement} The calendar view element.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #createCalendarView() {
        const container = document.createElement('div');

        container.className = 'datetime-picker-calendar';
        container.setAttribute('role', 'tabpanel');

        // Header.
        const header = document.createElement('div');
        header.className = 'datetime-picker-header';

        const monthYear = document.createElement('button');
        monthYear.type = 'button';
        monthYear.className = 'datetime-picker-month-year';
        monthYear.setAttribute('aria-label', this.#locales?.selectMonthAndYear || 'Selecteer maand en jaar');

        // Navigation controls container.
        const navControls = document.createElement('div');
        navControls.className = 'datetime-picker-nav-controls';

        const prevButton = document.createElement('button');
        prevButton.type = 'button';
        prevButton.className = 'datetime-picker-nav';

        const prevIcon = document.createElement('span');
        prevIcon.className = 'nav-icon';
        prevIcon.textContent = '‹';

        prevButton.replaceChildren(prevIcon);
        prevButton.setAttribute('aria-label', this.#locales?.previousMonth || 'Vorige maand');
        prevButton.setAttribute('data-nav', 'prev');

        // Today button in header.
        const todayButton = document.createElement('button');
        todayButton.type = 'button';
        todayButton.className = 'datetime-picker-today-button';
        todayButton.textContent = this.#locales?.today || 'Vandaag';

        const nextButton = document.createElement('button');
        nextButton.type = 'button';
        nextButton.className = 'datetime-picker-nav';

        const nextIcon = document.createElement('span');
        nextIcon.className = 'nav-icon';
        nextIcon.textContent = '›';

        nextButton.replaceChildren(nextIcon);
        nextButton.setAttribute('aria-label', this.#locales?.nextMonth || 'Volgende maand');
        nextButton.setAttribute('data-nav', 'next');

        navControls.appendChild(prevButton);
        navControls.appendChild(todayButton);
        navControls.appendChild(nextButton);

        header.appendChild(monthYear);
        header.appendChild(navControls);

        // Calendar grid.
        const grid = document.createElement('div');
        grid.className = 'datetime-picker-grid';
        grid.setAttribute('role', 'grid');

        container.appendChild(header);
        container.appendChild(grid);

        return container;
    }

    /**
     * Creates a day button for the calendar.
     * @param {Date} date The date for the button.
     * @param {boolean} otherMonth Whether this date is from another month.
     * @param {DocumentFragment} fragment The fragment to append to.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #createDayButton(date, otherMonth, fragment) {
        const button = this.#getOrCreateElement('dayButton', () => {
            const element = document.createElement('button');

            element.type = 'button';

            return element;
        });

        button.className = 'datetime-picker-day';
        button.textContent = date.getDate();
        button.dataset.date = this.#formatDate(date);

        // Add classes.
        if (otherMonth) {
            button.classList.add('other-month');
        }

        // Check if today.
        const today = new Date();

        if (this.#isSameDay(date, today)) {
            const todayText = this.#locales?.today || 'Vandaag';

            button.classList.add('today');
            button.setAttribute('aria-label', `${todayText}, ${this.#formatDateForAria(date)}`);
        }
        else {
            button.setAttribute('aria-label', this.#formatDateForAria(date));
        }

        // Check if selected.
        if (this.#selectedDate && this.#isSameDay(date, this.#selectedDate)) {
            button.classList.add('selected');
            button.setAttribute('aria-selected', 'true');
        }

        // Check if disabled.
        if (this.#isDateDisabled(date)) {
            button.classList.add('disabled');
            button.disabled = true;
        }

        fragment.appendChild(button);
    }

    /**
     * Creates the month selector overlay.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #createMonthSelector() {
        // Create the month selector overlay container.
        const monthSelector = document.createElement('div');
        monthSelector.className = 'datetime-picker-month-selector';

        // Create month grid.
        const monthGrid = document.createElement('div');
        monthGrid.className = 'datetime-picker-month-grid';
        monthGrid.setAttribute('role', 'grid');

        // Create month buttons using short month names.
        const monthsShort = this.#locales?.monthsShort || ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

        monthsShort.forEach((monthShort, index) => {
            const monthButton = document.createElement('button');
            monthButton.type = 'button';
            monthButton.className = 'datetime-picker-month-button';
            monthButton.textContent = monthShort;
            monthButton.setAttribute('role', 'gridcell');
            monthButton.setAttribute('data-month', index.toString());

            // Use full month name for aria-label.
            const months = this.#locales?.months || ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
            const fullMonthName = months[index];
            const selectText = this.#locales?.selectMonth || 'Selecteer';

            monthButton.setAttribute('aria-label', `${selectText} ${fullMonthName}`);

            monthGrid.appendChild(monthButton);
        });

        monthSelector.appendChild(monthGrid);

        // Add to calendar grid container for proper positioning over grid only.
        // First, we need to wrap the grid in a relative container.
        if (!this.#elements.calendarGrid.parentElement.classList.contains('datetime-picker-grid-container')) {
            const gridContainer = document.createElement('div');
            gridContainer.className = 'datetime-picker-grid-container';
            gridContainer.style.position = 'relative';
            
            // Move the grid into the container.
            this.#elements.calendarGrid.parentElement.insertBefore(gridContainer, this.#elements.calendarGrid);
            gridContainer.appendChild(this.#elements.calendarGrid);
        }
        
        // Add month selector to the grid container.
        this.#elements.calendarGrid.parentElement.appendChild(monthSelector);

        // Store reference.
        this.#elements.monthSelector = monthSelector;
        this.#elements.monthGrid = monthGrid;
        
        // Invalidate cached elements since DOM structure changed.
        this.#invalidateElementCache();

        // Add event listeners.
        this.#addListener(monthGrid, 'click', (event) => {
            if (event.target.classList.contains('datetime-picker-month-button')) {
                event.preventDefault();
                event.stopPropagation();

                const selectedMonth = parseInt(event.target.dataset.month);

                this.#selectMonth(selectedMonth);
            }
        });

        // Add keyboard navigation for month grid.
        this.#addListener(monthGrid, 'keydown', (event) => {
            if (event.target.classList.contains('datetime-picker-month-button')) {
                switch (event.key) {

                    case 'ArrowDown':
                    case 'ArrowLeft':
                    case 'ArrowRight':
                    case 'ArrowUp':
                        event.preventDefault();

                        this.#handleMonthGridNavigation(event, monthGrid);

                        break;

                    case 'Enter':
                    case ' ':
                        event.preventDefault();
                        event.stopPropagation();

                        this.#selectMonth(parseInt(event.target.dataset.month));

                        break;

                    case 'Escape':
                        event.preventDefault();

                        this.#hideMonthSelector();

                        break;

                    default:
                        break;
                
                }
            }
        });
    }

    /**
     * Creates the popup element.
     * @returns {HTMLElement} The popup element.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #createPopup() {
        const popup = document.createElement('div');
        popup.className = 'datetime-picker-popup';
        popup.setAttribute('role', 'dialog');
        popup.setAttribute('aria-hidden', 'true');

        const randomBase = 36;
        popup.id = popup.id || `dtp-${this.id || Math.random().toString(randomBase).slice(2)}`;

        // Add specific class for time-only mode.
        if (this.#mode === DateTimePicker.MODES.TIME) {
            popup.classList.add('datetime-picker-popup-time-only');
        }

        const hasDate = this.#mode !== DateTimePicker.MODES.TIME;
        const hasTime = this.#mode !== DateTimePicker.MODES.DATE;
        const hasMultipleTabs = hasDate && hasTime;

        // Create tabs only if both date and time are needed.
        let tabs;

        if (hasMultipleTabs) {
            tabs = document.createElement('div');
            tabs.className = 'datetime-picker-tabs';
            tabs.setAttribute('role', 'tablist');

            const calendarTab = document.createElement('button');
            calendarTab.type = 'button';
            calendarTab.className = 'datetime-picker-tab active';
            calendarTab.textContent = this.#locales?.dateTab || 'Datum';
            calendarTab.setAttribute('role', 'tab');
            calendarTab.setAttribute('data-tab', 'calendar');
            calendarTab.setAttribute('aria-selected', 'true');

            const timeTab = document.createElement('button');
            timeTab.type = 'button';
            timeTab.className = 'datetime-picker-tab';
            timeTab.textContent = this.#locales?.timeTab || 'Tijd';
            timeTab.setAttribute('role', 'tab');
            timeTab.setAttribute('data-tab', 'time');
            timeTab.setAttribute('aria-selected', 'false');

            tabs.appendChild(calendarTab);
            tabs.appendChild(timeTab);
        }

        // Create views based on mode.
        const views = [];

        if (hasDate) {
            views.push(this.#createCalendarView());
        }

        if (hasTime) {
            views.push(this.#createTimeView());
        }

        // Assemble.
        if (tabs) {
            popup.appendChild(tabs);
        }

        views.forEach((view) => {
            popup.appendChild(view);
        });

        // Add OK/Cancel buttons for modes that need confirmation.
        if (this.#mode === DateTimePicker.MODES.DATE_TIME || this.#mode === DateTimePicker.MODES.TIME) {
            const buttonContainer = document.createElement('div');
            buttonContainer.className = 'datetime-picker-buttons';

            const cancelButton = document.createElement('button');
            cancelButton.type = 'button';
            cancelButton.className = 'datetime-picker-button datetime-picker-cancel';
            cancelButton.textContent = this.#locales?.cancel || 'Annuleren';

            const okButton = document.createElement('button');
            okButton.type = 'button';
            okButton.className = 'datetime-picker-button datetime-picker-ok';
            okButton.textContent = this.#locales?.ok || 'OK';

            buttonContainer.appendChild(cancelButton);
            buttonContainer.appendChild(okButton);
            popup.appendChild(buttonContainer);
        }

        return popup;
    }

    /**
     * Creates the time view.
     * @returns {HTMLElement} The time view element.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #createTimeView() {
        const container = document.createElement('div');

        container.className = 'datetime-picker-time';
        container.setAttribute('role', 'tabpanel');

        // Only hide if there are multiple views.
        if (this.#mode === DateTimePicker.MODES.DATE_TIME) {
            container.style.display = 'none';
        }

        // Time picker header.
        const header = document.createElement('div');
        header.className = 'datetime-picker-time-header';

        // Current time display in header.
        const timeDisplay = document.createElement('div');
        timeDisplay.className = 'datetime-picker-time-header-display';
        timeDisplay.textContent = this.#showSeconds ? '00:00:00' : '00:00';

        // Now button.
        const nowButton = document.createElement('button');
        nowButton.type = 'button';
        nowButton.className = 'datetime-picker-now-button';
        nowButton.textContent = this.#locales?.now || 'Nu';
        nowButton.setAttribute('aria-label', this.#locales?.setToCurrentTime || 'Stel in op huidige tijd');

        header.appendChild(timeDisplay);
        header.appendChild(nowButton);

        // IOS-style wheel selectors container.
        const wheelContainer = document.createElement('div');
        wheelContainer.className = 'datetime-picker-wheel-container';

        // Add selection overlay.
        const selectionOverlay = document.createElement('div');
        selectionOverlay.className = 'datetime-picker-selection-overlay';
        wheelContainer.appendChild(selectionOverlay);

        // Create wheel selectors.
        const hoursWheel = this.#createTimeWheel('hours', DateTimePicker.HOURS_24, this.#locales?.hours || 'Uren');
        wheelContainer.appendChild(hoursWheel);

        // Add time separator.
        const separator1 = document.createElement('div');
        separator1.className = 'datetime-picker-time-separator';
        separator1.textContent = ':';
        wheelContainer.appendChild(separator1);

        const minutesWheel = this.#createTimeWheel('minutes', DateTimePicker.MINUTES, this.#locales?.minutes || 'Minuten');
        wheelContainer.appendChild(minutesWheel);

        if (this.#showSeconds) {
            // Add second separator.
            const separator2 = document.createElement('div');
            separator2.className = 'datetime-picker-time-separator';
            separator2.textContent = ':';
            wheelContainer.appendChild(separator2);

            const secondsWheel = this.#createTimeWheel('seconds', DateTimePicker.SECONDS, this.#locales?.seconds || 'Seconden');
            wheelContainer.appendChild(secondsWheel);
        }

        // AM/PM wheel for 12-hour format.
        const periodWheel = document.createElement('div');
        periodWheel.className = 'datetime-picker-wheel datetime-picker-period-wheel';
        periodWheel.style.display = 'none';
        periodWheel.setAttribute('data-time', 'period');

        const periodList = document.createElement('div');
        periodList.className = 'datetime-picker-wheel-list';

        const meridiem = this.#locales?.meridiem || ['AM', 'PM'];
        [meridiem[0], meridiem[1]].forEach((period, index) => {
            const item = document.createElement('div');
            item.className = 'datetime-picker-wheel-item';
            item.textContent = period;
            item.setAttribute('data-value', index);

            // Add click handler for direct selection.
            item.addEventListener('click', (event) => {
                event.preventDefault();

                const targetValue = parseInt(item.getAttribute('data-value'));

                this.#selectWheelValue(periodWheel, targetValue);
            });

            periodList.appendChild(item);
        });

        periodWheel.appendChild(periodList);
        wheelContainer.appendChild(periodWheel);

        container.appendChild(header);
        container.appendChild(wheelContainer);

        return container;
    }

    /**
     * Creates an iOS-style scrollable wheel selector for time values.
     * @param {string} type The time type (hours, minutes, seconds).
     * @param {number} max The maximum value.
     * @param {string} label The label text for accessibility.
     * @returns {HTMLElement} The wheel selector element.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #createTimeWheel(type, max, label) {
        const wheel = document.createElement('div');

        wheel.className = 'datetime-picker-wheel';
        wheel.setAttribute('data-time', type);
        wheel.setAttribute('role', 'listbox');
        wheel.setAttribute('aria-label', label);

        const list = document.createElement('div');
        list.className = 'datetime-picker-wheel-list';

        // Create options for the wheel.
        for (let i = 0; i <= max; i++) {
            const item = document.createElement('div');

            item.className = 'datetime-picker-wheel-item';
            item.textContent = i.toString().padStart(2, '0');
            item.setAttribute('data-value', i.toString());
            item.setAttribute('role', 'option');

            // Don't set any initial selection.
            item.setAttribute('aria-selected', 'false');

            // Add click handler for direct selection.
            item.addEventListener('click', (event) => {
                event.preventDefault();
                const targetValue = parseInt(item.getAttribute('data-value'));

                this.#selectWheelValue(wheel, targetValue);
            });

            list.appendChild(item);
        }

        wheel.appendChild(list);

        // Add scroll event listener for wheel selection.
        wheel.addEventListener('scroll', this.#handleWheelScroll.bind(this));

        // Add touch/mouse event listeners for better interaction.
        wheel.addEventListener('touchstart', this.#handleWheelTouchStart.bind(this));
        wheel.addEventListener('touchmove', this.#handleWheelTouchMove.bind(this));
        wheel.addEventListener('touchend', this.#handleWheelTouchEnd.bind(this));

        return wheel;
    }

    /**
     * Creates a week number cell for the calendar.
     * @param {Date} weekStartDate The start date of the week.
     * @param {DocumentFragment} fragment The fragment to append to.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #createWeekNumberCell(weekStartDate, fragment) {
        const weekNumber = this.#getWeekNumber(weekStartDate);
        const cell = this.#getOrCreateElement('weekNumber', () => {
            const element = document.createElement('div');

            element.setAttribute('role', 'rowheader');

            return element;
        });

        cell.className = 'datetime-picker-week-number';
        cell.textContent = weekNumber.toString();

        cell.setAttribute('aria-label', `${this.#locales?.week || 'wk'} ${weekNumber}`);

        fragment.appendChild(cell);
    }

    /**
     * Creates the year selector overlay.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #createYearSelector() {
        /* eslint-disable no-magic-numbers -- Date calculation magic. */
        // Create the year selector overlay container.
        const yearSelector = document.createElement('div');
        yearSelector.className = 'datetime-picker-year-selector';

        // Create year grid (4x3 grid for 12 years).
        const yearGrid = document.createElement('div');
        yearGrid.className = 'datetime-picker-year-grid';
        yearGrid.setAttribute('role', 'grid');

        // Calculate year range around displayed year for user context.
        // Use the actual selected date's year if available, otherwise fall back to current year.
        const displayedYear = this.#value ? this.#value.getFullYear() : this.#currentYear;
        const startYear = Math.floor(displayedYear / 12) * 12;
        
        // Create year buttons for 12 years.
        for (let i = 0; i < 12; i++) {
            const year = startYear + i;
            const selectYearText = this.#locales?.selectYear || 'Selecteer jaar';
            const yearButton = document.createElement('button');

            yearButton.type = 'button';
            yearButton.className = 'datetime-picker-year-button';
            yearButton.textContent = year.toString();
            yearButton.setAttribute('role', 'gridcell');
            yearButton.setAttribute('data-year', year.toString());
            yearButton.setAttribute('aria-label', `${selectYearText} ${year}`);

            yearGrid.appendChild(yearButton);
        }

        yearSelector.appendChild(yearGrid);

        // Add to calendar grid container for proper positioning.
        if (!this.#elements.calendarGrid.parentElement.classList.contains('datetime-picker-grid-container')) {
            const gridContainer = document.createElement('div');
            gridContainer.className = 'datetime-picker-grid-container';
            gridContainer.style.position = 'relative';
            
            // Move the grid into the container.
            this.#elements.calendarGrid.parentElement.insertBefore(gridContainer, this.#elements.calendarGrid);
            gridContainer.appendChild(this.#elements.calendarGrid);
        }
        
        // Add year selector to the grid container.
        this.#elements.calendarGrid.parentElement.appendChild(yearSelector);

        // Store reference.
        this.#elements.yearSelector = yearSelector;
        this.#elements.yearGrid = yearGrid;
        
        // Invalidate cached elements since DOM structure changed.
        this.#invalidateElementCache();

        // Add event listeners.
        this.#addListener(yearGrid, 'click', (event) => {
            if (event.target.classList.contains('datetime-picker-year-button')) {
                event.preventDefault();
                event.stopPropagation();

                const selectedYear = parseInt(event.target.dataset.year);

                this.#selectYear(selectedYear);
            }
        });

        // Add keyboard navigation for year grid.
        this.#addListener(yearGrid, 'keydown', (event) => {
            if (event.target.classList.contains('datetime-picker-year-button')) {
                switch (event.key) {

                    case 'ArrowDown':
                    case 'ArrowLeft':
                    case 'ArrowRight':
                    case 'ArrowUp':
                        event.preventDefault();

                        this.#handleYearGridNavigation(event, yearGrid);

                        break;

                    case 'Enter':
                    case ' ':
                        event.preventDefault();
                        event.stopPropagation();

                        this.#selectYear(parseInt(event.target.dataset.year));

                        break;

                    case 'Escape':
                        event.preventDefault();
                        this.#hideYearSelector();

                        break;

                    default:
                        break;
                
                }
            }
        });
        /* eslint-enable no-magic-numbers */
    }

    /**
     * Formats a date as date only string.
     * @param {Date} date The date to format.
     * @returns {string} The formatted date string.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #formatDate(date) {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }

    /**
     * Formats a date for aria labels.
     * @param {Date} date The date to format.
     * @returns {string} The formatted date string.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #formatDateForAria(date) {
        // Use the native toLocaleDateString with the component's locale.
        const options = { day: 'numeric', month: 'long', weekday: 'long', year: 'numeric' };

        return date.toLocaleDateString(this.locale, options);
    }

    /**
     * Formats a Date as ISO-like local string (YYYY-MM-DDTHH:mm:ss).
     * @param {Date} date The date to format.
     * @returns {string} The formatted datetime string.
     * @private
     * @memberof DateTimePicker
     */
    #formatISODateTime(date) {
        const pad = (number) => String(number).padStart(2, '0');

        return `${this.#formatDate(date) }T` +
               `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    }

    /**
     * Gets the default format based on the current mode.
     * @returns {string} The default format string.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #getDefaultFormat() {
        switch (this.#mode) {

            case DateTimePicker.MODES.DATE:
                return 'yyyy-MM-dd';

            case DateTimePicker.MODES.TIME:
                return this.#showSeconds ? 'HH:mm:ss' : 'HH:mm';

            case DateTimePicker.MODES.DATE_TIME:
            default:
                return this.#showSeconds ? 'yyyy-MM-dd HH:mm:ss' : 'yyyy-MM-dd HH:mm';

        }
    }

    /**
     * Gets the appropriate icon for the current mode.
     * @returns {Object} Object with icon type and value {isClass: boolean, icon: string}.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #getIconForMode() {
        const customIcon = this.getAttribute('toggle-icon');
        let defaultIcon;

        switch (this.#mode) {

            case DateTimePicker.MODES.TIME:
                defaultIcon = '🕐';

                break;

            case DateTimePicker.MODES.DATE:
            case DateTimePicker.MODES.DATE_TIME:
            default:
                defaultIcon = '📅';

                break;

        }

        return {
            icon: customIcon || defaultIcon,
            isClass: !!customIcon
        };
    }

    /**
     * Gets or creates a reusable DOM element from the cache.
     * 
     * @param {string} type - The type of element ('dayButton', 'weekHeader', etc.)
     * @param {Function} createFn - Function to create new element if not in cache
     * @returns {HTMLElement} The cached or newly created element
     * @private
     * @memberof DateTimePicker
     */
    #getOrCreateElement(type, createFn) {
        const cacheKey = type + 's';

        if (!this.#elementCache[cacheKey]) {
            this.#elementCache[cacheKey] = [];
        }

        const cached = this.#elementCache[cacheKey].pop();
        if (cached) {
            // Reset element to clean state
            cached.className = '';
            cached.textContent = '';
            cached.removeAttribute('aria-label');
            cached.removeAttribute('aria-selected');
            cached.removeAttribute('data-date');
            cached.removeAttribute('data-month');
            cached.removeAttribute('data-year');
            cached.disabled = false;

            return cached;
        }

        return createFn();
    }

    /**
     * Calculates the ISO week number for a given date.
     * @param {Date} date The date to calculate the week number for.
     * @returns {number} The ISO week number.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #getWeekNumber(date) {
        const target = new Date(date.valueOf());

        /* eslint-disable no-magic-numbers -- Date calculation magic. */
        const dayNumber = (date.getDay() + 6) % DateTimePicker.WEEKDAYS;

        target.setDate(target.getDate() - dayNumber + 3);

        const firstThursday = target.valueOf();

        target.setMonth(0, 1);

        if (target.getDay() !== 4) {
            target.setMonth(0, 1 + (((4 - target.getDay()) + DateTimePicker.WEEKDAYS) % DateTimePicker.WEEKDAYS));
        }

        return 1 + Math.ceil((firstThursday - target) / 604800000);
        /* eslint-enable no-magic-numbers */
    }

    /**
     * Hides the month selector overlay and returns to calendar view.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #hideMonthSelector() {
        if (this.#elements.monthSelector) {
            this.#elements.monthSelector.classList.remove('show');

            // Wait for transition to complete before hiding.
            // Match CSS transition duration.
            setTimeout(() => {
                if (!this.#elements.monthSelector.classList.contains('show')) {
                    this.#elements.monthSelector.style.display = 'none';
                }
            }, DateTimePicker.TRANSITION_TIMEOUT);
        }

        // Clear the flag.
        this.#isInMonthSelector = false;

        // Update month/year display back to full format.
        this.#renderCalendar();
    }

    /**
     * Hides the year selector overlay and returns to calendar view.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #hideYearSelector() {
        if (this.#elements.yearSelector) {
            this.#elements.yearSelector.classList.remove('show');

            // Wait for transition to complete before hiding.
            // Match CSS transition duration.
            setTimeout(() => {
                if (!this.#elements.yearSelector.classList.contains('show')) {
                    this.#elements.yearSelector.style.display = 'none';
                }
            }, DateTimePicker.TRANSITION_TIMEOUT);
        }

        // Clear the flag.
        this.#isInYearSelector = false;

        // Update month/year display back to full format.
        this.#renderCalendar();
    }

    /**
     * Invalidates cached DOM element references that may have changed.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #invalidateElementCache() {
        // Clear cached button references when DOM structure changes.
        this.#elements.monthButtons = null;
        this.#elements.yearButtons = null;
        this.#elements.firstFocusable = null;
        this.#elements.prevNav = null;
        this.#elements.nextNav = null;
    }

    /**
     * Checks if a date is disabled.
     * @param {Date} date The date to check.
     * @returns {boolean} True if the date is disabled.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #isDateDisabled(date) {
        if (this.#mode === DateTimePicker.MODES.DATE) {
            // For date-only mode, normalize the date being checked to start of day for comparison.
            const normalizedDate = this.#normalizeToStartOfDay(date);

            if (this.#normalizedMin && normalizedDate < this.#normalizedMin) {
                return true;
            }

            if (this.#normalizedMax && normalizedDate > this.#normalizedMax) {
                return true;
            }
        }
        else {
            // For datetime modes, use exact time comparisons.
            if (this.#min && date < this.#min) {
                return true;
            }

            if (this.#max && date > this.#max) {
                return true;
            }
        }

        return false;
    }

    /**
     * Checks if two dates are the same day.
     * @param {Date} date1 The first date.
     * @param {Date} date2 The second date.
     * @returns {boolean} True if the dates are the same day.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #isSameDay(date1, date2) {
        return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
    }

    /**
     * Navigates to a different month or year depending on current view.
     * @param {number} direction The direction to navigate (-1 for previous, 1 for next).
     * 
     * @private
     * @memberof DateTimePicker
     */
    #navigateDate(direction) {
        /* eslint-disable no-magic-numbers -- Date calculation magic. */
        if (this.#isInMonthSelector) {
            // In month selector mode, navigate years.
            this.#currentYear += direction;
            this.#elements.monthYearDisplay.textContent = this.#currentYear.toString();
        }
        else if (this.#isInYearSelector) {
            // In year selector mode, navigate year ranges (12 years at a time).
            this.#currentYear += direction * 12;

            const startYear = Math.floor(this.#currentYear / 12) * 12;
            const endYear = startYear + 11;

            this.#elements.monthYearDisplay.textContent = `${startYear} - ${endYear}`;
            
            // Update existing year selector buttons with new range.
            if (this.#elements.yearGrid) {
                // Clear existing buttons.
                this.#elements.yearGrid.innerHTML = '';
                
                // Create new year buttons for the new range.
                for (let i = 0; i < 12; i++) {
                    const year = startYear + i;
                    const selectYearText = this.#locales?.selectYear || 'Selecteer jaar';
                    const yearButton = document.createElement('button');

                    yearButton.type = 'button';
                    yearButton.className = 'datetime-picker-year-button';
                    yearButton.textContent = year.toString();
                    yearButton.setAttribute('role', 'gridcell');
                    yearButton.setAttribute('data-year', year.toString());
                    yearButton.setAttribute('aria-label', `${selectYearText} ${year}`);

                    this.#elements.yearGrid.appendChild(yearButton);
                }
            }
        }
        else {
            // In calendar mode, navigate months.
            this.#currentMonth += direction;

            if (this.#currentMonth < 0) {
                this.#currentMonth = DateTimePicker.MONTHS;
                this.#currentYear--;
            }
            else if (this.#currentMonth > DateTimePicker.MONTHS) {
                this.#currentMonth = 0;
                this.#currentYear++;
            }

            this.#renderCalendar();
        }
        /* eslint-enable no-magic-numbers */
    }

    /**
     * Normalizes a date to the end of the day (23:59:59.999).
     * @param {Date} date The date to normalize.
     * @returns {Date|null} A new Date object set to the end of the day, or null if input is null.
     *
     * @private
     * @memberof DateTimePicker
     */
    #normalizeToEndOfDay(date) {
        if (!date) {
            return null;
        }

        const endOfDayHour = 23;
        const endOfDayMinute = 59;
        const endOfDaySecond = 59;
        const endOfDayMillisecond = 999;
        const normalized = new Date(date);

        normalized.setHours(endOfDayHour, endOfDayMinute, endOfDaySecond, endOfDayMillisecond);

        return normalized;
    }

    /**
     * Normalizes a date to the start of the day (00:00:00.000).
     * @param {Date} date The date to normalize.
     * @returns {Date|null} A new Date object set to the start of the day, or null if input is null.
     *
     * @private
     * @memberof DateTimePicker
     */
    #normalizeToStartOfDay(date) {
        if (!date) {
            return null;
        }

        const normalized = new Date(date);
        normalized.setHours(0, 0, 0, 0);

        return normalized;
    }

    /**
     * Parses the current input value and updates internal state.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #parseInputValue() {
        const inputValue = this.#elements.input.value.trim();

        if (inputValue) {
            // Validate first.
            if (!this.#validateInputValue(inputValue)) {
                // If we have an existing value, preserve it instead of falling back to today.
                if (this.#value) {
                    this.#currentMonth = this.#value.getMonth();
                    this.#currentYear = this.#value.getFullYear();

                    return;
                }

                // Only fall back to today if we have no existing value.
                const now = new Date();
                this.#currentMonth = now.getMonth();
                this.#currentYear = now.getFullYear();

                return;
            }

            // Parse using the dating library.
            const parsedDate = this.#parseInputByFormat(inputValue);

            if (parsedDate && !isNaN(parsedDate.getTime())) {
                this.#value = parsedDate;
                this.#selectedDate = new Date(parsedDate);
                this.#selectedTime = {
                    hours: parsedDate.getHours(),
                    minutes: parsedDate.getMinutes(),
                    seconds: parsedDate.getSeconds()
                };

                this.#currentMonth = parsedDate.getMonth();
                this.#currentYear = parsedDate.getFullYear();
            }
        }
        else {
            // If input is empty, clear the value.
            this.#value = null;
            this.#selectedDate = null;
            this.#selectedTime = { hours: 0, minutes: 0, seconds: 0 };

            // Set current month/year to today for calendar display.
            const today = new Date();
            this.#currentMonth = today.getMonth();
            this.#currentYear = today.getFullYear();
        }

        // Update displays.
        this.#updateFromValue();
    }

    /**
     * Parses an input string according to the component's format.
     * @param {string} inputValue The input string to parse.
     * @returns {Date|null} The parsed date or null if parsing failed.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #parseInputByFormat(inputValue) {
        try {
            const parsedDateString = window.dating.parseDateString(inputValue, this.#format);
            const parsedDate = new Date(parsedDateString);

            // Verify the date is valid.
            if (isNaN(parsedDate.getTime())) {
                return null;
            }

            return parsedDate;
        }
        catch (error) {
            window.isDebugMode && console.warn('Date parsing error:', error);

            return null;
        }
    }

    /**
     * Dispatches a change event with enhanced payload.
     * @private
     * @memberof DateTimePicker
     */
    #dispatchChangeEvent() {
        const detail = {
            formattedValue: this.#value ? window.dating.formatDate(this.#value, this.#format) : '',
            mode: this.mode,
            value: this.value,
            valueAsDate: this.valueAsDate,
            valueAsLocalDate: this.valueAsLocalDate
        };

        this.dispatchEvent(new CustomEvent('change', { 
            bubbles: true, 
            composed: true,
            detail
        }));
    }

    /**
     * Parses a given ISO string to a javascript date object.
     * For date-only mode, ensures timezone-free parsing.
     * @param {String} val The ISO string date.
     * @returns {Date|null} The parsed date or null.
     */
    #parseISODate(val) {
        if (typeof val !== 'string') {
            return null;
        }

        let date = null;

        // First try parsing with the format from the attribute.
        if (this.hasAttribute('format')) {
            const attrFormat = this.getAttribute('format');

            if (attrFormat) {
                try {
                    const parsedDateString = window.dating.parseDateString(val, attrFormat);

                    date = new Date(parsedDateString);

                    if (isNaN(date.getTime())) {
                        date = null;
                    }
                }
                catch (error) {
                    date = null;
                }
            }
        }

        // If that fails, try parsing with the component's current format.
        if (!date && this.#format) {
            date = this.#parseInputByFormat(val);
        }

        // Use Date constructor but avoid bare date strings.
        if (!date) {
            date = new Date(val);

            if (isNaN(date.getTime())) {
                date = null;
            }
        }

        return date;
    }

    /**
     * Positions the popup relative to the input element.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #positionPopup() {
        const inputRect = this.#elements.wrapper.getBoundingClientRect();
        const popupRect = this.#elements.popup.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const scrollX = window.scrollX || document.documentElement.scrollLeft;
        const scrollY = window.scrollY || document.documentElement.scrollTop;

        // Calculate available space above and below.
        const spaceBelow = viewportHeight - inputRect.bottom;
        const spaceAbove = inputRect.top;

        // Estimated height if not rendered yet.
        const popupHeight = popupRect.height || DateTimePicker.MIN_DIALOG_HEIGHT;

        // Determine if popup should show above or below.
        const showAbove = spaceBelow < popupHeight && spaceAbove > spaceBelow;

        // Calculate horizontal position.
        let left = inputRect.left + scrollX;

        // Estimated width if not rendered yet.
        const popupWidth = popupRect.width || DateTimePicker.MIN_DIALOG_WIDTH;

        // Adjust if popup would overflow viewport.
        if (left + popupWidth > viewportWidth + scrollX) {
            left = viewportWidth + scrollX - popupWidth - DateTimePicker.EDGE_OFFSET;
        }

        // Ensure popup doesn't go off the left edge.
        if (left < scrollX + DateTimePicker.EDGE_OFFSET) {
            left = scrollX + DateTimePicker.EDGE_OFFSET;
        }

        // Calculate vertical position.
        let top;

        if (showAbove) {
            top = inputRect.top + scrollY - popupHeight - DateTimePicker.SCROLL_OFFSET;
            this.#elements.popup.classList.add('above');
        }
        else {
            top = inputRect.bottom + scrollY + DateTimePicker.SCROLL_OFFSET;
            this.#elements.popup.classList.remove('above');
        }

        // Apply positioning.
        this.#elements.popup.style.position = 'absolute';
        this.#elements.popup.style.left = `${left}px`;
        this.#elements.popup.style.top = `${top}px`;
        this.#elements.popup.style.zIndex = '9999';
        
        // Set width for time-only mode to match input wrapper.
        if (this.#mode === DateTimePicker.MODES.TIME) {
            this.#elements.popup.style.width = this.#elements.wrapper.offsetWidth + 'px';
        }
    }

    /**
     * Removes all tracked event listeners.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #removeEventListeners() {
        this.#listeners.forEach((handlers, element) => {
            handlers.forEach(({ event, handler }) => {
                element.removeEventListener(event, handler);
            });
        });

        this.#listeners.clear();
    }

    /**
     * Renders the component HTML.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #render() {
        this.#updateComponentClasses();

        // Create wrapper.
        const wrapper = document.createElement('div');
        wrapper.className = 'datetime-picker-wrapper';

        // Create input.
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'datetime-picker-input';
        input.name = this.getAttribute('name') || '';
        input.setAttribute('aria-haspopup', 'dialog');
        input.setAttribute('aria-expanded', 'false');
        
        // Create toggle button.
        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'datetime-picker-toggle';
        
        // Set accessible label based on mode.
        let ariaLabel = '';

        switch (this.#mode) {
            case DateTimePicker.MODES.DATE:
                ariaLabel = this.#locales?.openDatePicker || 'Open datumkiezer';

                break;

            case DateTimePicker.MODES.TIME:
                ariaLabel = this.#locales?.openTimePicker || 'Open tijdkiezer';

                break;

            case DateTimePicker.MODES.DATE_TIME:
                ariaLabel = this.#locales?.openDateTimePicker || 'Open datum/tijd kiezer';

                break;

            default:
                ariaLabel = this.#locales?.openPicker || 'Open kiezer';

                break;
        }

        toggle.setAttribute('aria-label', ariaLabel);

        // Apply disabled state.
        if (this.disabled) {
            input.disabled = true;
            toggle.disabled = true;
        }

        // Apply read-only state.
        if (this.readOnly) {
            input.readOnly = true;
        }

        const iconData = this.#getIconForMode();
        const span = document.createElement('span');
        span.className = 'datetime-picker-icon';

        if (iconData.isClass) {
            span.classList.add(iconData.icon);
        }
        else {
            span.textContent = iconData.icon;
        }

        toggle.replaceChildren(span);

        // Create popup.
        const popup = this.#createPopup();

        // Assemble.
        wrapper.appendChild(input);
        wrapper.appendChild(toggle);

        this.appendChild(wrapper);

        // Attach popup to document body for proper positioning.
        document.body.appendChild(popup);

        // Store element references.
        this.#elements = {
            calendarGrid: popup.querySelector('.datetime-picker-grid'),
            calendarTab: popup.querySelector('[data-tab="calendar"]'),
            calendarView: popup.querySelector('.datetime-picker-calendar'),
            cancelButton: popup.querySelector('.datetime-picker-cancel'),
            hoursInput: popup.querySelector('[data-time="hours"]'),
            input,
            minutesInput: popup.querySelector('[data-time="minutes"]'),
            monthGrid: null,
            monthSelector: null,
            monthYearDisplay: popup.querySelector('.datetime-picker-month-year'),
            nowButton: popup.querySelector('.datetime-picker-now-button'),
            okButton: popup.querySelector('.datetime-picker-ok'),
            periodToggle: popup.querySelector('.datetime-picker-period-toggle'),
            popup,
            secondsInput: popup.querySelector('[data-time="seconds"]'),
            tabs: popup.querySelector('.datetime-picker-tabs'),
            timeDisplay: popup.querySelector('.datetime-picker-time-header-display'),
            timeTab: popup.querySelector('[data-tab="time"]'),
            timeView: popup.querySelector('.datetime-picker-time'),
            todayButton: popup.querySelector('.datetime-picker-today-button'),
            toggle,
            wrapper
        };

        // Link input to popup with aria-controls.
        input.setAttribute('aria-controls', popup.id);

        // Apply initial states.
        this.#updateDisabledState();
        this.#updateReadonlyState();
        this.#updateLocalizedContent();
        this.#updateNormalizedBounds();
    }

    /**
     * Renders the calendar grid with optimized element reuse.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #renderCalendar() {
        if (!this.#elements.calendarGrid || !this.#elements.monthYearDisplay) {
            return;
        }

        // Update month/year display only when not in year selector mode.
        if (!this.#isInYearSelector) {
            const months = this.#locales?.months || ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];

            this.#elements.monthYearDisplay.textContent = `${months[this.#currentMonth]} ${this.#currentYear}`;
        }

        // Preserve focus information before clearing.
        const activeElement = document.activeElement;
        let focusedDateString = null;

        if (activeElement && activeElement.classList.contains('datetime-picker-day')) {
            focusedDateString = activeElement.dataset.date;
        }

        // Cache existing elements before clearing.
        const existingElements = Array.from(this.#elements.calendarGrid.children);

        existingElements.forEach((element) => {
            if (element.classList.contains('datetime-picker-day')) {
                this.#returnToCache('dayButton', element);
            }
            else if (element.classList.contains('datetime-picker-weekday')) {
                this.#returnToCache('weekHeader', element);
            }
            else if (element.classList.contains('datetime-picker-week-header')) {
                this.#returnToCache('weekHeader', element);
            }
            else if (element.classList.contains('datetime-picker-week-number')) {
                this.#returnToCache('weekNumber', element);
            }

            // Remove from DOM but keep in cache.
            element.remove();
        });

        if (this.#showWeekNumbers) {
            this.#elements.calendarGrid.classList.add('show-week-numbers');
        }

        // Calculate week start based on locale.
        const firstDayOfWeek = this.#locales?.firstDayOfWeek || 0;
        const weekDaysMin = this.#locales?.weekDaysMin || ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];
        const weekDays = [...weekDaysMin];

        // Reorder weekdays based on first day of week.
        for (let i = 0; i < firstDayOfWeek; i++) {
            weekDays.push(weekDays.shift());
        }

        // Use document fragment for efficient DOM manipulation.
        const fragment = document.createDocumentFragment();

        // Add week header if week numbers are enabled.
        if (this.#showWeekNumbers) {
            const weekHeader = this.#getOrCreateElement('weekHeader', () => {
                const element = document.createElement('div');

                element.setAttribute('role', 'columnheader');

                return element;
            });
            
            weekHeader.className = 'datetime-picker-week-header';
            weekHeader.textContent = this.#locales?.week || 'wk';
            fragment.appendChild(weekHeader);
        }

        // Add weekday headers using element cache.
        weekDays.forEach((day) => {
            const header = this.#getOrCreateElement('weekHeader', () => {
                const element = document.createElement('div');

                element.setAttribute('role', 'columnheader');

                return element;
            });

            header.className = 'datetime-picker-weekday';
            header.textContent = day;
            fragment.appendChild(header);
        });

        // Calculate days.
        const firstDay = new Date(this.#currentYear, this.#currentMonth, 1);
        const lastDay = new Date(this.#currentYear, this.#currentMonth + 1, 0);
        const prevMonthLastDay = new Date(this.#currentYear, this.#currentMonth, 0);

        let startingDayOfWeek = firstDay.getDay() - firstDayOfWeek;

        if (startingDayOfWeek < 0) {
            startingDayOfWeek += DateTimePicker.WEEKDAYS;
        }

        const daysInMonth = lastDay.getDate();
        const daysInPrevMonth = prevMonthLastDay.getDate();

        // Track current week for week number calculations.
        let cellCount = 0,
            currentWeekStartDate = null;

        // Previous month's days.
        for (let i = startingDayOfWeek - 1; i >= 0; i--) {
            const day = daysInPrevMonth - i;
            const date = new Date(this.#currentYear, this.#currentMonth - 1, day);

            // Add week number for the first cell of each week.
            if (this.#showWeekNumbers && cellCount % DateTimePicker.WEEKDAYS === 0) {
                currentWeekStartDate = new Date(date);
                this.#createWeekNumberCell(currentWeekStartDate, fragment);
            }

            this.#createDayButton(date, true, fragment);
            cellCount++;
        }

        // Current month's days.
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(this.#currentYear, this.#currentMonth, day);

            // Add week number for the first cell of each week.
            if (this.#showWeekNumbers && cellCount % DateTimePicker.WEEKDAYS === 0) {
                currentWeekStartDate = new Date(date);
                this.#createWeekNumberCell(currentWeekStartDate, fragment);
            }

            this.#createDayButton(date, false, fragment);
            cellCount++;
        }

        // Next month's days.
        const remainingCells = DateTimePicker.MONTH_GRID_SIZE - (startingDayOfWeek + daysInMonth);

        for (let day = 1; day <= remainingCells; day++) {
            const date = new Date(this.#currentYear, this.#currentMonth + 1, day);

            // Add week number for the first cell of each week.
            if (this.#showWeekNumbers && cellCount % DateTimePicker.WEEKDAYS === 0) {
                currentWeekStartDate = new Date(date);
                this.#createWeekNumberCell(currentWeekStartDate, fragment);
            }

            this.#createDayButton(date, true, fragment);
            cellCount++;
        }
        
        // Append all elements at once (better performance).
        this.#elements.calendarGrid.appendChild(fragment);

        // Restore focus if a date was previously focused.
        if (focusedDateString) {
            setTimeout(() => {
                const targetElement = this.#elements.calendarGrid.querySelector(`[data-date="${focusedDateString}"]`);

                if (targetElement && !targetElement.disabled) {
                    targetElement.focus();
                }
                else {
                    // If the focused date is now disabled, find the nearest enabled date.
                    const enabledButtons = Array.from(this.#elements.calendarGrid.querySelectorAll('.datetime-picker-day:not([disabled])'));

                    if (enabledButtons.length > 0) {
                        // Find the nearest enabled date to the previously focused one.
                        const focusedDate = new Date(focusedDateString);

                        let minDistance = Math.abs(new Date(enabledButtons[0].dataset.date) - focusedDate);
                        let nearestButton = enabledButtons[0];

                        for (const button of enabledButtons) {
                            const buttonDate = new Date(button.dataset.date);
                            const distance = Math.abs(buttonDate - focusedDate);

                            if (distance < minDistance) {
                                minDistance = distance;
                                nearestButton = button;
                            }
                        }

                        nearestButton.focus();
                    }
                }
            }, 0);
        }
    }

    /**
     * Returns an element to the cache for reuse.
     * 
     * @param {string} type - The type of element ('dayButton', 'weekHeader', etc.)
     * @param {HTMLElement} element - The element to cache
     * @private
     * @memberof DateTimePicker
     */
    #returnToCache(type, element) {
        const cacheKey = type + 's';
        if (!this.#elementCache[cacheKey]) {
            this.#elementCache[cacheKey] = [];
        }

        this.#elementCache[cacheKey].push(element);
    }

    /**
     * Selects a date.
     * @param {Date} date The date to select.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #selectDate(date) {
        this.#selectedDate = date;

        // Update value.
        if (this.#value) {
            this.#value.setFullYear(date.getFullYear());
            this.#value.setMonth(date.getMonth());
            this.#value.setDate(date.getDate());
        }
        else {
            this.#value = new Date(date);

            // For date-only mode, set time to start of day.
            if (this.#mode === DateTimePicker.MODES.DATE) {
                this.#value.setHours(0, 0, 0, 0);
            }
        }

        // Update display.
        this.#updateInputDisplay();

        if (this.#elements.calendarGrid) {
            this.#renderCalendar();
        }

        // Dispatch change event.
        this.#dispatchChangeEvent();

        // For date-time mode, switch to time view. For date-only mode, close picker.
        if (this.#mode === DateTimePicker.MODES.DATE_TIME) {
            this.#setActiveView('time');
        }
        else if (this.#mode === DateTimePicker.MODES.DATE) {
            this.close();
        }
    }

    /**
     * Selects a month and returns to calendar view.
     * @param {number} monthIndex The selected month index (0-11).
     * 
     * @private
     * @memberof DateTimePicker
     */
    #selectMonth(monthIndex) {
        // Update current month.
        this.#currentMonth = monthIndex;

        // Hide month selector and return to calendar view.
        this.#hideMonthSelector();
    }

    /**
     * Selects today's date.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #selectToday() {
        const today = new Date();

        // Update current month and year to today's values.
        this.#currentMonth = today.getMonth();
        this.#currentYear = today.getFullYear();

        this.#selectDate(today);
    }

    /**
     * Selects a specific value on a wheel and scrolls to it.
     * @param {HTMLElement} wheel The wheel element.
     * @param {number} value The value to select.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #selectWheelValue(wheel, value) {
        const timeType = wheel.getAttribute('data-time');

        // Handle AM/PM period wheel separately.
        if (wheel.classList.contains('datetime-picker-period-wheel')) {
            this.#selectedTime.period = value;
        }
        else if (timeType) {
            this.#selectedTime[timeType] = value;
        }
        else {
            return;
        }

        // Update displays immediately.
        this.#updateTimeDisplay();
        this.#updateValue();

        // Scroll to the selected value.
        const list = wheel.querySelector('.datetime-picker-wheel-list');
        const items = wheel.querySelectorAll('.datetime-picker-wheel-item');
        const itemHeight = items[0]?.offsetHeight || DateTimePicker.WHEEL_OFFSET_HEIGHT;
        const wheelHeight = wheel.offsetHeight;
        const listPadding = parseFloat(getComputedStyle(list).paddingTop) || 0;

        // Calculate target scroll position to center the selected item.
        const centerOffset = (wheelHeight / 2) - (itemHeight / 2);
        const targetScrollTop = (value * itemHeight) + listPadding - centerOffset;

        wheel.scrollTo({ behavior: 'smooth', top: targetScrollTop });

        // Update visual selection.
        this.#updateWheelSelection(wheel, false);
    }

    /**
     * Selects a year and returns to calendar view.
     * @param {number} yearIndex The selected year.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #selectYear(yearIndex) {
        // Update current year.
        this.#currentYear = yearIndex;

        // Hide year selector and return to calendar view.
        this.#hideYearSelector();
    }

    /**
     * Sets the active view (calendar or time).
     * @param {string} view The view to activate.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #setActiveView(view) {
        this.#activeView = view;

        // Only update tabs if they exist.
        if (this.#elements.calendarTab && this.#elements.timeTab) {
            this.#elements.calendarTab.classList.toggle('active', view === 'calendar');
            this.#elements.calendarTab.setAttribute('aria-selected', view === 'calendar');
            this.#elements.timeTab.classList.toggle('active', view === 'time');
            this.#elements.timeTab.setAttribute('aria-selected', view === 'time');
        }

        // Update views.
        if (this.#elements.calendarView) {
            this.#elements.calendarView.style.display = view === 'calendar' ? 'block' : 'none';
        }

        if (this.#elements.timeView) {
            this.#elements.timeView.style.display = view === 'time' ? 'flex' : 'none';
            
            // Update wheel positions when switching to time view.
            if (view === 'time') {
                setTimeout(() => {
                    this.#updateWheelPositions();
                }, 100);
            }
        }
        
        this.#updateFromValue();

        // Set min/max sizes for the popup so size does not change (skip for time-only mode).
        if (this.#mode !== DateTimePicker.MODES.TIME) {
            this.#elements.popup.style.maxBlockSize = this.#elements.popup.offsetHeight + 'px';
            this.#elements.popup.style.minBlockSize = this.#elements.popup.offsetHeight + 'px';
            this.#elements.popup.style.maxInlineSize = this.#elements.popup.offsetWidth + 'px';
            this.#elements.popup.style.minInlineSize = this.#elements.popup.offsetWidth + 'px';
        }
        
        // On another view we might need to move the popup.
        this.#positionPopup();
    }

    /**
     * Shows the month selector overlay over the calendar view.
     * Navigation remains visible and functional at the top.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #showMonthYearSelector() {
        // If month selector doesn't exist, create it.
        if (!this.#elements.monthSelector) {
            this.#createMonthSelector();
        }
        
        // Update the month/year display to show only the year.
        this.#elements.monthYearDisplay.textContent = this.#currentYear.toString();

        // Set flag to indicate we're in month selection mode.
        this.#isInYearSelector = false;
        this.#isInMonthSelector = true;

        // Show the month selector overlay with smooth transition.
        this.#elements.monthSelector.style.display = 'block';

        // Force reflow to ensure display:block is applied before transition.
        this.#elements.monthSelector.offsetHeight;
        this.#elements.monthSelector.classList.add('show');

        // Update month buttons selection state.
        this.#updateMonthSelectorButtons();

        // Focus the current month button.
        setTimeout(() => {
            const selectedMonth = this.#elements.monthSelector.querySelector('.selected') ||
                this.#elements.monthSelector.querySelector('.datetime-picker-month-button');

            if (selectedMonth) {
                selectedMonth.focus();
            }
        }, 0);
    }

    /**
     * Shows the year selector overlay over the calendar view.
     * Navigation remains visible and functional at the top.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #showYearSelector() {
        // Use the actual selected date's year if available, otherwise fall back to current year.
        const displayedYear = this.#value ? this.#value.getFullYear() : this.#currentYear;
        const startYear = Math.floor(displayedYear / DateTimePicker.YEAR_GRID) * DateTimePicker.YEAR_GRID;
        
        // Check if year selector exists and covers the right range.
        let needsRecreation = false;

        if (this.#elements.yearSelector) {
            const firstButton = this.#elements.yearGrid.querySelector('.datetime-picker-year-button');

            if (firstButton) {
                const currentStartYear = parseInt(firstButton.dataset.year);
                needsRecreation = (currentStartYear !== startYear);
            }
        }
        
        // If year selector doesn't exist or needs recreation, create/recreate it.
        if (!this.#elements.yearSelector || needsRecreation) {
            // Remove existing year selector if it exists.
            if (this.#elements.yearSelector) {
                this.#elements.yearSelector.remove();
                this.#elements.yearSelector = null;
                this.#elements.yearGrid = null;
            }

            this.#createYearSelector();
        }
        
        // Update the month/year display to show year range.
        const endYear = startYear + DateTimePicker.MONTHS;
        this.#elements.monthYearDisplay.textContent = `${startYear} - ${endYear}`;

        // Set flag to indicate we're in year selection mode.
        this.#isInMonthSelector = false;
        this.#isInYearSelector = true;

        // Show the year selector overlay with smooth transition.
        this.#elements.yearSelector.style.display = 'block';

        // Force reflow to ensure display:block is applied before transition.
        this.#elements.yearSelector.offsetHeight;
        this.#elements.yearSelector.classList.add('show');

        // Update year buttons selection state.
        this.#updateYearSelectorButtons();

        // Focus the current year button.
        setTimeout(() => {
            const selectedYear = this.#elements.yearSelector.querySelector('.selected') ||
                this.#elements.yearSelector.querySelector('.datetime-picker-year-button');

            if (selectedYear) {
                selectedYear.focus();
            }
        }, 0);
    }

    /**
    * Snaps a wheel to the currently selected item position.
    * @param {HTMLElement} wheel The wheel element.
    * 
    * @private
    * @memberof DateTimePicker
    */
    #snapWheelToSelection(wheel) {
        const list = wheel.querySelector('.datetime-picker-wheel-list');
        const items = list.querySelectorAll('.datetime-picker-wheel-item');
        const selectedItem = list.querySelector('.datetime-picker-wheel-item.selected');

        if (!selectedItem) {
            return;
        }

        const itemHeight = items[0].offsetHeight;
        const wheelHeight = wheel.offsetHeight;
        const selectedIndex = Array.from(items).indexOf(selectedItem);
        const listPadding = parseFloat(getComputedStyle(list).paddingTop) || 0;

        // Calculate target scroll position to center the selected item.
        const centerOffset = (wheelHeight / 2) - (itemHeight / 2);
        const targetScrollTop = (selectedIndex * itemHeight) + listPadding - centerOffset;

        wheel.scrollTo({ behavior: 'smooth', top: targetScrollTop });
    }

    /**
     * Toggles between AM and PM.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #togglePeriod() {
        const hours = this.#value ? this.#value.getHours() : 0;
        const newHours = hours < DateTimePicker.NOON ? hours + DateTimePicker.NOON : hours - DateTimePicker.NOON;

        if (this.#value) {
            this.#value.setHours(newHours);
            this.#selectedTime.hours = newHours;

            this.#updateTimeDisplay();
            this.#updateInputDisplay();

            // Dispatch change event.
            this.#dispatchChangeEvent();
        }
    }

    /**
     * Updates component CSS classes.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #updateComponentClasses() {
        this.className = `datetime-picker datetime-picker-${this.#mode}`;
    }

    /**
     * Updates the disabled state of the component.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #updateDisabledState() {
        const disabled = this.disabled;

        if (this.#elements.input) {
            this.#elements.input.disabled = disabled;
            this.#elements.toggle.disabled = disabled;
        }
    }

    /**
     * Updates internal state from the current value.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #updateFromValue() {
        if (this.#value) {
            this.#selectedDate = new Date(this.#value);
            this.#selectedTime = {
                hours: this.#value.getHours(),
                minutes: this.#value.getMinutes(),
                seconds: this.#value.getSeconds()
            };

            this.#currentMonth = this.#value.getMonth();
            this.#currentYear = this.#value.getFullYear();
        }

        this.#updateInputDisplay();

        if (this.#elements.calendarGrid) {
            this.#renderCalendar();
        }

        if (this.#elements.timeDisplay) {
            this.#updateTimeDisplay();
        }
    }

    /**
     * Updates the input display with the formatted datetime.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #updateInputDisplay() {
        if (!this.#elements.input) {
            return;
        }

        if (this.#value) {
            const source = this.#mode === DateTimePicker.MODES.DATE
                         ? new Date(this.#value.getFullYear(), this.#value.getMonth(), this.#value.getDate())
                         : this.#value;
            this.#elements.input.value = window.dating.formatDate(source, this.#format);
        }
        else {
            this.#elements.input.value = '';
        }
    }
    
    /**
     * Updates localized content in the UI.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #updateLocalizedContent() {
        // Update placeholder and aria-label if not explicitly set.
        if (!this.hasAttribute('placeholder') && this.#elements.input) {
            let placeholder;

            switch (this.#mode) {

                case DateTimePicker.MODES.DATE:
                    placeholder = this.#locales?.selectDate || 'dd-mm-jjjj';

                    break;

                case DateTimePicker.MODES.TIME:
                    placeholder = this.#locales?.selectTime || 'HH:mm';

                    break;

                case DateTimePicker.MODES.DATE_TIME:
                default:
                    placeholder = this.#locales?.selectDateTime || 'd-M-jjjj HH:mm';

                    break;

            }

            this.#elements.input.placeholder = placeholder;
            this.#elements.popup.setAttribute('aria-label', placeholder);
        }

        // Update navigation buttons.
        const prevButton = this.#elements.popup?.querySelector('[data-nav="prev"]');
        const nextButton = this.#elements.popup?.querySelector('[data-nav="next"]');

        if (prevButton) {
            prevButton.setAttribute('aria-label', this.#locales?.previousMonth || 'Vorige maand');
        }

        if (nextButton) {
            nextButton.setAttribute('aria-label', this.#locales?.nextMonth || 'Volgende maand');
        }

        // Update today button.
        if (this.#elements.todayButton) {
            this.#elements.todayButton.textContent = this.#locales?.today || 'Vandaag';
        }

        // Update period toggle if visible.
        if (this.#elements.periodToggle && this.#elements.periodToggle.style.display !== 'none') {
            const { hours } = this.#selectedTime;
            const periodIndex = hours < DateTimePicker.NOON ? 0 : 1;
            const meridiem = this.#locales?.meridiem || ['AM', 'PM'];

            this.#elements.periodToggle.textContent = meridiem[periodIndex];
        }
    }

    /**
     * Updates UI elements based on the current mode.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #updateModeUI() {
        if (!this.#elements.popup) {
            return;
        }

        const hasDate = this.#mode !== DateTimePicker.MODES.TIME;
        const hasTime = this.#mode !== DateTimePicker.MODES.DATE;
        const hasMultipleTabs = hasDate && hasTime;

        // Show/hide tabs container.
        if (this.#elements.tabs) {
            this.#elements.tabs.style.display = hasMultipleTabs ? 'flex' : 'none';
        }

        // Show/hide calendar tab.
        if (this.#elements.calendarTab) {
            this.#elements.calendarTab.style.display = hasDate ? 'block' : 'none';
        }

        // Show/hide time tab.
        if (this.#elements.timeTab) {
            this.#elements.timeTab.style.display = hasTime ? 'block' : 'none';
        }

        // Show/hide calendar view.
        if (this.#elements.calendarView) {
            this.#elements.calendarView.style.display = hasDate ? 'block' : 'none';
        }

        // Show/hide time view.
        if (this.#elements.timeView) {
            this.#elements.timeView.style.display = hasTime ? 'block' : 'none';
        }

        // Update format if it wasn't explicitly set.
        if (!this.hasAttribute('format')) {
            this.#format = this.#getDefaultFormat();
            this.#updateInputDisplay();
        }

        // Update icon based on mode.
        if (this.#elements.toggle) {
            const iconData = this.#getIconForMode();
            const span = document.createElement('span');

            span.className = 'datetime-picker-icon';

            if (iconData.isClass) {
                span.className += ` ${iconData.icon}`;
                span.classList.add(iconData.icon);
            }
            else {
                span.textContent = iconData.icon;
            }

            this.#elements.toggle.replaceChildren(span);
        }

        // Add mode class to component.
        this.#updateComponentClasses();
    }

    /**
     * Updates the month selector buttons' selection state.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #updateMonthSelectorButtons() {
        if (!this.#elements.monthGrid) {
            return;
        }

        const monthButtons = this.#elements.monthGrid.querySelectorAll('.datetime-picker-month-button');

        monthButtons.forEach((button, index) => {
            if (index === this.#currentMonth) {
                button.classList.add('selected');
                button.setAttribute('aria-selected', 'true');
            }
            else {
                button.classList.remove('selected');
                button.setAttribute('aria-selected', 'false');
            }
        });
    }

    /**
     * Updates the cached normalized min/max values for date-only mode comparisons.
     * @returns {boolean} True if the normalized bounds changed, false otherwise.
     *
     * @private
     * @memberof DateTimePicker
     */
    #updateNormalizedBounds() {
        const oldNormalizedMin = this.#normalizedMin;
        const oldNormalizedMax = this.#normalizedMax;

        if (this.#mode === DateTimePicker.MODES.DATE) {
            // For date-only mode, normalize to day boundaries.
            this.#normalizedMin = this.#normalizeToStartOfDay(this.#min);
            this.#normalizedMax = this.#normalizeToEndOfDay(this.#max);
        }
        else {
            // For datetime modes, use the original values.
            this.#normalizedMin = this.#min;
            this.#normalizedMax = this.#max;
        }

        // Return whether bounds actually changed.
        const minChanged = (oldNormalizedMin?.getTime() || null) !== (this.#normalizedMin?.getTime() || null);
        const maxChanged = (oldNormalizedMax?.getTime() || null) !== (this.#normalizedMax?.getTime() || null);

        return minChanged || maxChanged;
    }

    /**
     * Updates the readonly state of the component.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #updateReadonlyState() {
        const readonly = this.readOnly;

        if (this.#elements.input) {
            this.#elements.input.readOnly = readonly;
        }
    }

    /**
     * Updates the time display.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #updateTimeDisplay() {
        if (!this.#elements.timeDisplay) {
            return;
        }

        const { hours } = this.#selectedTime;
        const { minutes } = this.#selectedTime;
        const { seconds } = this.#selectedTime;

        // Update display.
        const format = this.#format.includes('a') || this.#format.includes('A');

        let displayHours = hours,
            periodIndex = 0;

        if (format) {
            if (hours === 0) {
                displayHours = DateTimePicker.NOON;
            }
            else if (hours > DateTimePicker.NOON) {
                displayHours = hours - DateTimePicker.NOON;
                periodIndex = 1;
            }
            else if (hours === DateTimePicker.NOON) {
                periodIndex = 1;
            }

            if (this.#elements.periodToggle) {
                const meridiem = this.#locales?.meridiem || ['AM', 'PM'];

                this.#elements.periodToggle.style.display = 'block';
                this.#elements.periodToggle.textContent = meridiem[periodIndex];
            }
        }
        else if (this.#elements.periodToggle) {
            this.#elements.periodToggle.style.display = 'none';
        }

        let timeString = `${String(displayHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

        if (this.#showSeconds) {
            timeString += `:${String(seconds).padStart(2, '0')}`;
        }

        this.#elements.timeDisplay.textContent = timeString;

        // Update inputs.
        if (this.#elements.hoursInput) {
            this.#elements.hoursInput.value = hours;
        }

        if (this.#elements.minutesInput) {
            this.#elements.minutesInput.value = minutes;
        }

        if (this.#elements.secondsInput) {
            this.#elements.secondsInput.value = seconds;
        }
    }

    /**
     * Updates the time UI based on show-seconds setting.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #updateTimeUI() {
        if (!this.#elements.secondsInput) {
            return;
        }

        const secondsGroup = this.#elements.secondsInput.closest('.datetime-picker-time-group');

        if (secondsGroup) {
            secondsGroup.style.display = this.#showSeconds ? 'block' : 'none';
        }

        // Update time display format.
        if (this.#elements.timeDisplay && !this.#showSeconds) {
            const { hours, minutes } = this.#selectedTime;
            const timeString = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

            this.#elements.timeDisplay.textContent = timeString;
        }
    }

    /**
     * Updates the component's value from selected date and time.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #updateValue() {
        let newValue = null;

        // Combine selected date and time based on mode.
        if (this.#mode === DateTimePicker.MODES.DATE) {
            // Date only mode.
            if (this.#selectedDate) {
                newValue = new Date(this.#selectedDate);
            }
        }
        else if (this.#mode === DateTimePicker.MODES.DATE_TIME) {
            // Date-time mode.
            if (this.#selectedDate) {
                newValue = new Date(this.#selectedDate.getFullYear(),
                                    this.#selectedDate.getMonth(),
                                    this.#selectedDate.getDate(),
                                    this.#selectedTime.hours || 0,
                                    this.#selectedTime.minutes || 0,
                                    this.#selectedTime.seconds || 0);
            }
        }
        else if (this.#mode === DateTimePicker.MODES.TIME) {
            // Time only mode - use today's date with selected time.
            const today = new Date();

            newValue = new Date(today.getFullYear(),
                                today.getMonth(),
                                today.getDate(),
                                this.#selectedTime.hours || 0,
                                this.#selectedTime.minutes || 0,
                                this.#selectedTime.seconds || 0);
        }

        // Update the value if it's different.
        if (newValue && (!this.#value || newValue.getTime() !== this.#value.getTime())) {
            this.#value = newValue;
            this.#updateInputDisplay();
            
            // Dispatch change event.
            this.#dispatchChangeEvent();
        }
    }

    /**
     * Updates week numbers UI based on the current setting.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #updateWeekNumbersUI() {
        this.#updateComponentClasses();

        // Re-render calendar if it's visible.
        if (this.#activeView === 'calendar' && this.#isOpen && this.#elements.calendarGrid) {
            this.#renderCalendar();
        }
    }

    /**
     * Updates wheel positions to match the current selected time.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #updateWheelPositions() {
        if (!this.#elements.timeView) {
            return;
        }

        const wheels = this.#elements.timeView.querySelectorAll('.datetime-picker-wheel');
        wheels.forEach((wheel) => {
            const timeType = wheel.getAttribute('data-time');

            if (timeType && this.#selectedTime[timeType] !== undefined) {
                const value = this.#selectedTime[timeType];
                const list = wheel.querySelector('.datetime-picker-wheel-list');
                const items = wheel.querySelectorAll('.datetime-picker-wheel-item');
                const itemHeight = items[0]?.offsetHeight || DateTimePicker.WHEEL_OFFSET_HEIGHT;
                const wheelHeight = wheel.offsetHeight;
                const listPadding = parseFloat(getComputedStyle(list).paddingTop) || 0;

                // Calculate target scroll position to center the selected item.
                const centerOffset = (wheelHeight / 2) - (itemHeight / 2);
                const targetScrollTop = (value * itemHeight) + listPadding - centerOffset;

                wheel.scrollTo({ behavior: 'smooth', top: targetScrollTop });

                // Update selection states.
                items.forEach((item, index) => {
                    const itemValue = parseInt(item.getAttribute('data-value'));

                    item.classList.toggle('selected', itemValue === value);
                    item.setAttribute('aria-selected', itemValue === value ? 'true' : 'false');
                });
            }
        });
    }

    /**
     * Updates the selected value in a wheel based on its scroll position.
     * @param {HTMLElement} wheel The wheel element.
     * @param {boolean} setTimeValue Whether to actually set the time value (default true).
     * 
     * @private
     * @memberof DateTimePicker
     */
    #updateWheelSelection(wheel, setTimeValue = true) {
        const list = wheel.querySelector('.datetime-picker-wheel-list');
        const items = list.querySelectorAll('.datetime-picker-wheel-item');

        if (items.length === 0) {
            return;
        }

        const itemHeight = items[0].offsetHeight;
        const wheelHeight = wheel.offsetHeight;
        const { scrollTop } = wheel;
        const listPadding = parseFloat(getComputedStyle(list).paddingTop) || 0;

        // Calculate which item is closest to center (accounting for padding).
        const centerOffset = (wheelHeight / 2) - (itemHeight / 2);
        const adjustedScrollTop = scrollTop - listPadding + centerOffset;
        let selectedIndex = Math.round(adjustedScrollTop / itemHeight);

        selectedIndex = Math.max(0, Math.min(selectedIndex, items.length - 1));

        // Update selection states.
        items.forEach((item, index) => {
            item.classList.toggle('selected', index === selectedIndex);
            item.setAttribute('aria-selected', index === selectedIndex ? 'true' : 'false');
        });

        // Don't snap during scroll events to avoid infinite scrolling.
        // Always update the time display for visual feedback, but only set input value if requested.
        const timeType = wheel.getAttribute('data-time');
        const selectedItem = items[selectedIndex];

        if (timeType && selectedItem) {
            const value = parseInt(selectedItem.getAttribute('data-value'));
            if (!isNaN(value)) {
                this.#selectedTime[timeType] = value;
                this.#updateTimeDisplay();

                // Only update the input value if explicitly requested.
                if (setTimeValue) {
                    this.#updateValue();
                }
            }
        }
    }

    /**
     * Updates the year selector buttons' selection state.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #updateYearSelectorButtons() {
        if (!this.#elements.yearGrid) {
            return;
        }

        // Use cached year buttons if available.
        const yearButtons = this.#elements.yearButtons || this.#elements.yearGrid.querySelectorAll('.datetime-picker-year-button');

        // Use the actual selected date's year if available, otherwise fall back to current year.
        const selectedYear = this.#value ? this.#value.getFullYear() : this.#currentYear;

        yearButtons.forEach((button) => {
            const year = parseInt(button.dataset.year);

            if (year === selectedYear) {
                button.classList.add('selected');
                button.setAttribute('aria-selected', 'true');
            }
            else {
                button.classList.remove('selected');
                button.setAttribute('aria-selected', 'false');
            }
        });
    }

    /**
     * Validates input value according to the component's format and mode.
     * @param {string} inputValue The input value to validate.
     * @returns {boolean} True if the input value is valid.
     * 
     * @private
     * @memberof DateTimePicker
     */
    #validateInputValue(inputValue) {
        // Determine the validation format based on the picker mode.
        let validationFormat;

        switch (this.#mode) {

            case DateTimePicker.MODES.DATE:
                validationFormat = 'date';

                break;

            case DateTimePicker.MODES.TIME:
                validationFormat = 'time';

                break;

            case DateTimePicker.MODES.DATE_TIME:
            default:
                validationFormat = 'both';

                break;

        }

        // Use the dating library's validation.
        const validationResult = window.dating.isDateStr(inputValue, validationFormat);

        // Empty string means valid.
        return validationResult === '';
    }
    // #endregion ********************************************************************************/

}
// #endregion ************************************************************************************/

/**
 * Register web component.
 * Defines the custom element 'datetime-picker' and maps it to the DateTimePicker class.
 */
customElements.define(DateTimePicker.tagName, DateTimePicker);
